import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ShoppingCart, Package, FileText, Truck, CheckCircle2, AlertTriangle,
  Plus, Search, X, Loader2, Building2, ClipboardList, Receipt,
  ArrowRight, BadgeCheck, Clock, DollarSign, TrendingUp, Banknote,
  ChevronDown, ChevronRight, Eye, BarChart2, MapPin, Filter,
  Check, RotateCcw, Calendar, AlertCircle, Boxes, MessageSquare,
  Download, Printer, History, FileCheck, Pencil, Paperclip, UploadCloud,
  List, BookOpen, Tag, ExternalLink, Brain, Sparkles, FileUp, CheckCheck, Zap, Trash2, Cloud, Link2, GripVertical
} from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatPYGCompact, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { toast } from "sonner";
import { Modal, StatCard, StatusBadge, StatusTimeline, PedidoDetailModal, descargarPedidoPDF } from "./SharedComponents";
import { FLOW_STEPS, STATUS_COLORS, STATUS_LABELS, UNITS, WORK_FRONTS, URGENCY_LABELS } from "./constants";
import { formatDateTime, buildHistory, generatePedidoPDF, generatePPPDF, generateAPPDF, generateRetiroStockPDF, splitFrenteCod } from "./helpers";
import { ImportPMItemsModal } from "./ImportPMItemsModal";
import { FiltroClienteObraPresupuesto, OBRA_RAPIDA_FILTER, filtrarPresupuestos } from "./FiltroClienteObraPresupuesto";
import SelectBuscable from "../../components/SelectBuscable";   // Fix DY (2026-09-23)
import { sortPendingFirst } from "./BusquedaFilters";
import { roleAllowed } from "../../utils/roles";
import { disponibleCentral } from "./AsignacionStock";   // Fix GF (2026-10-15)

// BLOQUE 63 — id estable de fila (React key), nunca se manda al backend.
let __pmItemSeq = 0;
// Fix A (2026-08-27): la categoría por defecto pasa de "menores" a "listado".
// El caso normal del residente es pedir contra un ítem del presupuesto; con
// "menores" la descripción quedaba como texto libre y el selector de ítems
// no aparecía nunca. Si no hay presupuesto seleccionado, PMItemRow (línea 59)
// ya filtra la opción "listado", así que este default es seguro.
// Fix G (2026-08-30): `tarea_numero` y `observacion` por ítem, ambos opcionales,
// pedidos en el documento del cliente del 29/08 (puntos 6 y 8). Viajan como
// parte del dict del ítem, que se copia entero de PM a PP, AP, PF y OC.
const newPMItem = () => ({ __uid: `pm-${Date.now()}-${++__pmItemSeq}`, categoria: "listado", item_uid: null, item_uids: null, material_origen_uids: null, material_name: "", quantity: 1, unit: "m", marca_recomendada: "", front: "", sector: "", nivel_id: "", tarea_numero: "", observacion: "" });
const stripUid = (items) => items.map(({ __uid, item_uids, material_origen_uids, ...rest }) => rest);
// Fix DL (2026-09-21): id de cada fila para arrastrarla.
export const pmRowId = (it, i) => (it && it.__uid) || `pm-row-${i}`;
// Fix DL (2026-09-21): mueve la fila `activeId` al lugar de `overId`.
export const moverFilaPM = (items, activeId, overId) => {
  const ids = items.map(pmRowId);
  const de = ids.indexOf(activeId);
  const a = ids.indexOf(overId);
  if (de < 0 || a < 0 || de === a) return items;
  return arrayMove(items, de, a);
};

// Fix DG (2026-09-16): "como se distribuye la cantidad total solicitada, el sistema
// debe sumar la cantidad total de X material contemplado en cada item, ese total
// seria el 100%. la cantidad en cada item tendria una carga porcentual sobre ese
// 100%, esa carga de cada item se multiplica por el total solicitado y es lo que se
// le imputa al item" (Edson, 16/09). El 100% son los ítems MARCADOS, no el
// presupuesto entero: si se marcan 3 de 6 y se piden 250, tomar el porcentaje sobre
// el presupuesto imputaría 125 y dejaría 125 sin imputar. La última parte absorbe el
// redondeo, así la suma da exactamente lo pedido. Sin pesos (material de texto libre
// que no está en el listado explotado), reparte en partes iguales, como hasta ahora.
export const repartirPorPesos = (total, uids, pesos) => {
  const t = Number(total) || 0;
  const n = uids.length;
  if (!n) return [];
  const w = uids.map(u => Math.max(0, Number((pesos || {})[u]) || 0));
  const suma = w.reduce((a, b) => a + b, 0);
  const frac = suma > 0 ? w.map(x => x / suma) : uids.map(() => 1 / n);
  const r = frac.map(f => Math.round(t * f * 10000) / 10000);
  const acum = r.slice(0, n - 1).reduce((a, b) => a + b, 0);
  r[n - 1] = Math.round((t - acum) * 10000) / 10000;
  return r;
};

// Fix DG (2026-09-16): "debe ser multi seleccion, no que cree otra fila como ahora
// mismo" (Edson, 16/09). En el formulario la fila queda UNA sola con los ítems
// marcados; recién al guardar se abre en una línea por ítem, que es lo que el
// pedido, la aprobación, la OC y el consumo saben leer. El nivel de cada línea sale
// del ítem al que se imputa —ya no se elige a mano— y la unidad también.
export const expandirFilas = (items, pesosPorMaterial, delPto) => {
  const byUid = {};
  (delPto || []).forEach(bi => { byUid[bi.item_uid] = bi; });
  const out = [];
  (items || []).forEach(({ item_uids, ...it }) => {
    const uids = Array.isArray(item_uids) ? item_uids.filter(Boolean) : [];
    if (uids.length < 2) { out.push(it); return; }
    const partes = repartirPorPesos(it.quantity, uids, (pesosPorMaterial || {})[it.material_name] || null);
    uids.forEach((uid, i) => {
      const bi = byUid[uid];
      out.push({
        ...it,
        __uid: `pm-${Date.now()}-${++__pmItemSeq}-${i}`,
        item_uid: uid,
        quantity: partes[i],
        nivel_id: bi && bi.nivel_id ? bi.nivel_id : "",
        // Fix DM (2026-09-21): la unidad es la del material, la que se ve en la fila.
        // Antes se reemplazaba en silencio por la del ítem del presupuesto (120 m de
        // caño se guardaban como 3 × 40 "un"). La del ítem queda solo si la fila no
        // tiene unidad.
        unit: it.unit || (bi && bi.unidad ? normalizarUM(bi.unidad) : ""),
      });
    });
  });
  return out;
};
// Fix S (2026-09-01): formulario en blanco, reutilizado al cerrar el modal, al
// terminar de guardar y al confirmar un borrador — para no repetir el objeto.
const blankPMForm = () => ({
  dias_uso_estimado: "", urgency: "normal", target_date: "", notes: "",
  lista_cliente_id: "", lista_obra_id: "", pto_objetivo: "", presupuesto_id: "", presupuesto_codigo: "",
  obra_rapida: false, obra_rapida_nombre: "",
  // Fix Z (2026-09-02): orden de trabajo de asistencia a la que se imputa el pedido.
  asistencia_id: "", asistencia_number: "",
  items: [newPMItem()],
});
// Fix AD (2026-09-03): "al clicar fuera de esa ventana sale y no queda guardado
// nada" (Edson, 03/09). Antes de avisar hay que saber si el formulario tiene algo
// que perder: un modal recién abierto se cierra sin preguntar, como siempre.
const formTieneContenido = (f) => {
  if (!f) return false;
  if ((f.items || []).some(i => (i.material_name || "").trim())) return true;
  return ["lista_cliente_id", "lista_obra_id", "presupuesto_id", "asistencia_id", "notes",
          "target_date", "obra_rapida_nombre", "dias_uso_estimado"].some(k => (f[k] || "").toString().trim());
};

// Fix AD (2026-09-03): respaldo del pedido a medio cargar, en el navegador. NO
// crea borradores en el sistema (Edson: "para que no se llene de borradores"):
// es la red para el caso que no pasa por ningún botón — se cierra la pestaña, se
// apaga el celular, se recarga la página. Todo en try/catch: si el navegador
// tiene el almacenamiento bloqueado, la app se comporta como hoy.
const PM_BORRADOR_LOCAL_KEY = "cen.pm.borrador_local.v1";
const PM_BORRADOR_LOCAL_DIAS = 7;

const guardarRespaldoLocal = (form, userId) => {
  try {
    localStorage.setItem(PM_BORRADOR_LOCAL_KEY, JSON.stringify({ form, at: new Date().toISOString(), user: userId || "" }));
  } catch (_e) { /* sin respaldo, el aviso de cierre sigue funcionando */ }
};

const borrarRespaldoLocal = () => {
  try { localStorage.removeItem(PM_BORRADOR_LOCAL_KEY); } catch (_e) { /* ignore */ }
};

const leerRespaldoLocal = (userId) => {
  try {
    const raw = localStorage.getItem(PM_BORRADOR_LOCAL_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || !d.form || !formTieneContenido(d.form)) return null;
    if ((d.user || "") !== (userId || "")) return null;
    const dias = (Date.now() - new Date(d.at).getTime()) / 86400000;
    if (!(dias >= 0) || dias > PM_BORRADOR_LOCAL_DIAS) return null;
    return d;
  } catch (_e) { return null; }
};

// Fix R (2026-08-31): etiqueta de un material explotado (sin número de ítem,
// a diferencia de budgetItemLabel — así se distinguen visualmente en el mismo
// desplegable: primero los materiales desglosados, después las líneas del
// presupuesto).
export const materialExplotadoLabel = (m) => `${m.descripcion} (${m.unidad})`;

// CEN 2.0 FASE 3 PARTE A — categoría obligatoria por ítem del pedido (medición de
// consumo por concepto). "listado" referencia un ítem del presupuesto (item_uid).
// Fix S (2026-09-01): nombres pedidos por el cliente el 01/09, textuales.
// Los VALORES no cambian: "listado", "menores", "imprevistos" y "equipamientos"
// son los que valida el backend y los que llevan los pedidos ya guardados.
// Fix CU (2026-09-14): "en pedido de materiales tenemos que tener todas estas
// categorías" (Edson, 14/09). Faltaban EPI y Alquiler: el backend ya las acepta
// (`PM_ITEM_CATEGORIAS`) y la hoja de compras de la obra ya las muestra, pero el
// desplegable del pedido se había quedado con seis. De paso los nombres pasan a ser
// los mismos que usa la obra, para que sea una sola nomenclatura en todo el ERP.
// Los VALORES no cambian: son los que valida el backend y los que llevan los pedidos
// ya guardados.
export const PM_CATEGORIAS = [
  { value: "listado", label: "Material (del Pto.)" },
  // Fix BD (2026-09-07): material de obra sin presupuesto vinculado todavía
  // (obra rápida). Disponible siempre, a diferencia de "listado". Se imputa
  // después, desde "Imputar compras anteriores" de la obra.
  { value: "materiales", label: "Material" },
  { value: "menores", label: "Material Menor" },
  { value: "imprevistos", label: "Imprevisto" },
  // Fix EI (2026-09-27): "que de equipamiento para abajo se agregue (Adm)" (CEN).
  { value: "equipamientos", label: "Equipamiento (Adm)" },
  // Fix X (2026-09-02): pedido de Edson del 02/09 ("Herramienta falta agregar en CATEG.").
  { value: "herramientas", label: "Herramienta (Adm)" },
  { value: "epis", label: "EPI (Adm)" },
  { value: "alquileres", label: "Alquiler (Adm)" },
];
export const budgetItemLabel = (bi) => `${bi.numero ? bi.numero + " — " : ""}${bi.descripcion} (${bi.unidad})`;

// Fix DG (2026-09-16): el nivel del ítem, para mostrarlo al lado de su descripción.
// Devuelve null si el ítem no tiene nivel asignado, y entonces no se pinta nada.
export const nivelDeItem = (bi, nivelMap) => (bi && bi.nivel_id && nivelMap ? nivelMap[bi.nivel_id] || null : null);
export const ChipNivel = ({ label }) => label
  ? <span className="ml-1 px-1 py-px rounded bg-[#EBF3FB] text-[#1A4B7A] text-[9px] font-bold align-middle">{label}</span>
  : null;

// Fix AM (2026-09-04): "poder filtrar todo ese despliegue para agilizar la
// busqueda (…) que a la hora de aplicar el filtro siempre se vea el encabezado y
// abajo sus subitems" (Edson, 04/09). El <select> nativo no se puede filtrar y no
// muestra de qué sección cuelga cada línea: en un presupuesto con "Bocas de
// luces" repetido en cada planta, hay que saberlo de memoria. Guarda el mismo
// valor (item_uid) que el desplegable anterior.
export function SelectItemPto({ value, itemsDeEsteMaterial, itemsRestantes, nivelMap, disabled, placeholder, testId, onChange }) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const inputRef = useRef(null);

  // Fix DG (2026-09-16): "en nativus hay una planta tipo que cuenta con 7 niveles
  // iguales. entonces al lado de esas copias generadas figure el nivel (…) De esta
  // manera estariamos seleccionando el item de la planta correcta y en un paso ya
  // estamos seleccionando item y nivel" (Edson, 16/09). Las copias las genera
  // Asignar Niveles → Distribuir en varios niveles y cada una ya guarda su nivel_id;
  // acá solo se muestra. Sin esto las 7 copias salen con el mismo texto y no hay
  // forma de saber cuál es cuál.
  const etiqueta = (bi) => `${bi.numero ? bi.numero + " " : ""}${bi.descripcion}`;
  const elegido = [...itemsDeEsteMaterial, ...itemsRestantes].find(bi => bi.item_uid === value) || null;

  const abrir = () => {
    if (disabled) return;
    const r = btnRef.current.getBoundingClientRect();
    const alto = Math.min(360, window.innerHeight - 32);
    const cabeAbajo = window.innerHeight - r.bottom > alto + 8;
    const ancho = Math.max(r.width, 460);
    setPos({
      left: Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8)),
      top: cabeAbajo ? r.bottom + 4 : Math.max(8, r.top - alto - 4),
      width: ancho, alto,
    });
    setQ("");
    setAbierto(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const term = q.trim().toLowerCase();
  const pasa = (bi) => !term
    || `${bi.numero || ""} ${bi.descripcion || ""}`.toLowerCase().includes(term)
    || `${bi.seccion_numero || ""} ${bi.seccion || ""}`.toLowerCase().includes(term);

  // Las líneas del presupuesto se agrupan por su sección, en el orden en que
  // vienen (que es el del cómputo), y el encabezado queda arriba de las suyas.
  const grupos = [];
  if (itemsDeEsteMaterial.filter(pasa).length > 0) {
    // Fix BS (2026-09-09): "en este despliegue no estan saliendo los encabezados
    // del presupuesto, dice 'DE ESTE MATERIAL' nomas (…) deberían salir para
    // facilitar la imputación correcta" (Edson, 09/09). Este grupo junta líneas
    // de secciones distintas con la misma descripción, así que sin la sección no
    // hay forma de saber cuál es la del Departamento B y cuál la del H.
    grupos.push({ clave: "__mat__", titulo: "De este material", conSeccion: true, items: itemsDeEsteMaterial.filter(pasa) });
  }
  const porSeccion = new Map();
  itemsRestantes.filter(pasa).forEach(bi => {
    const clave = `${bi.seccion_numero || ""}|${bi.seccion || ""}`;
    if (!porSeccion.has(clave)) {
      porSeccion.set(clave, {
        clave,
        titulo: [bi.seccion_numero, bi.seccion].filter(Boolean).join("  ") || "Sin sección",
        items: [],
      });
    }
    porSeccion.get(clave).items.push(bi);
  });
  grupos.push(...porSeccion.values());
  const nRes = grupos.reduce((s, g) => s + g.items.length, 0);

  return (
    <>
      <button type="button" ref={btnRef} onClick={abrir} disabled={disabled}
        className={`w-full text-left border rounded-lg px-2 py-1.5 text-[11px] bg-white disabled:bg-gray-100 flex items-start justify-between gap-1 ${!value && !disabled ? "border-amber-300 bg-amber-50/40" : "border-gray-300"}`}
        data-testid={testId}>
        <span className={`flex-1 whitespace-normal break-words ${elegido ? "text-gray-800" : "text-gray-400"}`}>
          {elegido ? <>{etiqueta(elegido)}<ChipNivel label={nivelDeItem(elegido, nivelMap)} /></> : placeholder}
        </span>
        <span className="text-gray-400 flex-shrink-0">▾</span>
      </button>
      {abierto && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setAbierto(false)} onWheel={() => setAbierto(false)} />
          <div className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col overflow-hidden"
            style={{ left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.alto }}
            data-testid={`${testId}-panel`}>
            <div className="flex items-center gap-1.5 px-2 py-2 border-b border-gray-100">
              <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); setAbierto(false); } }}
                placeholder="Filtrar por ítem o sección (ej. luces, azotea)..." className="w-full text-[11px] outline-none"
                data-testid={`${testId}-buscar`} />
              <span className="text-[10px] text-gray-400 flex-shrink-0">{nRes}</span>
            </div>
            <div className="overflow-y-auto">
              <button type="button" onClick={() => { onChange(""); setAbierto(false); }}
                className="w-full text-left px-3 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50">{placeholder}</button>
              {grupos.map(g => (
                <div key={g.clave}>
                  <p className="px-3 py-1 text-[10px] font-bold text-[#1A4B7A] uppercase tracking-wide bg-[#EBF3FB]">{g.titulo}</p>
                  {g.items.map(bi => (
                    <button key={bi.item_uid} type="button" onClick={() => { onChange(bi.item_uid); setAbierto(false); }}
                      className={`w-full text-left pl-6 pr-3 py-1.5 text-[11px] whitespace-normal break-words hover:bg-[#EBF3FB] ${bi.item_uid === value ? "bg-emerald-50 font-semibold text-emerald-800" : "text-gray-700"}`}>
                      {/* Fix BS (2026-09-09): en "De este material" cada línea dice de
                          qué sección del presupuesto viene. En los grupos por sección
                          ya está en el encabezado y repetirla sería ruido. */}
                      {g.conSeccion && (bi.seccion || bi.seccion_numero) && (
                        <span className="block text-[9px] font-bold text-[#1A4B7A]/70 uppercase leading-tight">
                          {[bi.seccion_numero, bi.seccion].filter(Boolean).join(" ")}
                        </span>
                      )}
                      {etiqueta(bi)}
                      <ChipNivel label={nivelDeItem(bi, nivelMap)} />
                    </button>
                  ))}
                </div>
              ))}
              {nRes === 0 && <p className="px-3 py-4 text-[11px] text-gray-400 text-center">Sin coincidencias</p>}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}


// Fix BR (2026-09-09): "podemos habilitar multiselección en la columna ITEM DEL
// PTO. y que siempre divida la cantidad solicitada entre la cantidad de items
// seleccionados, eso a modo de no estar repitiendo un material varias veces en
// una solicitud" (Edson, 09/09). Mismo criterio del Fix AN pero por ítem del
// presupuesto. Se agrupa y se busca igual que en SelectItemPto, porque con 304
// ítems y siete líneas con la misma descripción la sección es lo único que
// permite saber cuál es cuál.
// Fix FH (2026-10-08): "una vez pedido «seleccionar varios» ítems no te permite más marcar
// un solo ítem en el caso de que finalmente solo se necesite marcar uno" (CEN). Alcanza con
// uno: con uno solo la fila vuelve a quedar imputada a ese ítem (setItemsMultiples).
// También lo usa el pedido de servicios (`servicio`): ahí no hay material, se reparte en
// partes iguales.
export function VariosItemsPanel({ itemsDeEsteMaterial, itemsRestantes, nivelMap, pesos, cantidad, unidad, seleccion, onCancel, onConfirm, servicio = false }) {
  const [sel, setSel] = useState(() => Array.isArray(seleccion) ? seleccion.filter(Boolean) : []);
  const [q, setQ] = useState("");
  const toggle = (uid) => setSel(p => p.includes(uid) ? p.filter(x => x !== uid) : [...p, uid]);

  const term = q.trim().toLowerCase();
  const pasa = (bi) => !term
    || `${bi.numero || ""} ${bi.descripcion || ""}`.toLowerCase().includes(term)
    || `${bi.seccion_numero || ""} ${bi.seccion || ""}`.toLowerCase().includes(term);

  const grupos = [];
  const deEste = itemsDeEsteMaterial.filter(pasa);
  if (deEste.length > 0) grupos.push({ clave: "__mat__", titulo: "De este material", items: deEste });
  const porSeccion = new Map();
  itemsRestantes.filter(pasa).forEach(bi => {
    const clave = `${bi.seccion_numero || ""}|${bi.seccion || ""}`;
    if (!porSeccion.has(clave)) {
      porSeccion.set(clave, {
        clave,
        titulo: [bi.seccion_numero, bi.seccion].filter(Boolean).join("  ") || "Sin sección",
        items: [],
      });
    }
    porSeccion.get(clave).items.push(bi);
  });
  grupos.push(...porSeccion.values());
  const nRes = grupos.reduce((s, g) => s + g.items.length, 0);

  const total = Number(cantidad) || 0;
  // Fix DG (2026-09-16): la vista previa ya no dice "partes iguales": muestra lo que
  // de verdad le va a tocar a cada ítem según lo que ese ítem tiene contemplado del
  // material en el presupuesto.
  const partes = repartirPorPesos(total, sel, pesos);
  const todosLosItems = [...itemsDeEsteMaterial, ...itemsRestantes];
  const detalle = sel.map((uid, i) => {
    const bi = todosLosItems.find(x => x.item_uid === uid);
    const pct = total > 0 ? Math.round((partes[i] / total) * 1000) / 10 : 0;
    return { uid, bi, cant: partes[i], pct };
  });
  const sinPesos = !pesos || !sel.some(u => Number((pesos || {})[u]) > 0);

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="pm-varios-items-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-full overflow-hidden flex flex-col p-5 gap-3">
        <div>
          <h4 className="text-sm font-bold text-gray-900">Imputar a varios ítems del presupuesto</h4>
          <p className="text-[11px] text-gray-500 mt-0.5">{servicio
            ? "La cantidad pedida se reparte en partes iguales entre los ítems marcados. La fila del pedido sigue siendo una sola."
            : "La cantidad pedida se reparte entre los ítems marcados según cuánto de este material tiene contemplado cada uno. La fila del pedido sigue siendo una sola."}</p>
        </div>
        <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2 py-1.5">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filtrar por ítem o sección (ej. departamento h, 1x10A)..."
            className="w-full text-[11px] outline-none" data-testid="pm-varios-items-buscar" />
          <span className="text-[10px] text-gray-400 flex-shrink-0">{nRes}</span>
        </div>
        <div className="border border-gray-200 rounded-xl flex-1 min-h-[120px] max-h-64 overflow-y-auto">
          {grupos.map(g => (
            <div key={g.clave}>
              <p className="px-3 py-1 text-[10px] font-bold text-[#1A4B7A] uppercase tracking-wide bg-[#EBF3FB] sticky top-0">{g.titulo}</p>
              {g.items.map(bi => (
                <label key={bi.item_uid} className="flex items-start gap-2 px-3 py-1.5 text-[11px] cursor-pointer hover:bg-gray-50 border-b border-gray-50">
                  <input type="checkbox" checked={sel.includes(bi.item_uid)} onChange={() => toggle(bi.item_uid)}
                    className="w-3.5 h-3.5 mt-0.5 accent-[#2E6DA4] flex-shrink-0" data-testid={`pm-varios-item-${bi.item_uid}`} />
                  <span className="text-gray-700 whitespace-normal break-words">
                    {(bi.seccion || bi.seccion_numero) && (
                      <span className="block text-[9px] font-bold text-[#1A4B7A]/70 uppercase leading-tight">
                        {[bi.seccion_numero, bi.seccion].filter(Boolean).join(" ")}
                      </span>
                    )}
                    {bi.numero ? bi.numero + " " : ""}{bi.descripcion}
                    {bi.unidad ? <span className="text-gray-400"> ({bi.unidad})</span> : null}
                    <ChipNivel label={nivelDeItem(bi, nivelMap)} />
                  </span>
                </label>
              ))}
            </div>
          ))}
          {nRes === 0 && <p className="px-3 py-6 text-[11px] text-gray-400 text-center">Sin coincidencias</p>}
        </div>
        {sel.length >= 2 && (
          <div className="border border-gray-200 rounded-xl max-h-32 overflow-y-auto" data-testid="pm-varios-items-reparto">
            {detalle.map(d => (
              <div key={d.uid} className="flex items-start gap-2 px-3 py-1 text-[10px] border-b border-gray-50 last:border-b-0">
                <span className="flex-1 text-gray-600 whitespace-normal break-words">
                  {d.bi ? `${d.bi.numero ? d.bi.numero + " " : ""}${d.bi.descripcion}` : d.uid}
                  <ChipNivel label={nivelDeItem(d.bi, nivelMap)} />
                </span>
                <span className="flex-shrink-0 font-bold text-[#1A4B7A]">{d.cant} {unidad || ""}</span>
                <span className="flex-shrink-0 w-10 text-right text-gray-400">{d.pct}%</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-gray-600" data-testid="pm-varios-items-texto">
          {sel.length < 1
            ? "Marcá al menos 1 ítem del presupuesto."
            : sel.length === 1
              ? `${total} ${unidad || ""} van a ese ítem.`
              : servicio
                ? `${total} ${unidad || ""} repartidos en partes iguales.`
                : sinPesos
                  ? `Este material no figura en el listado del presupuesto, así que ${total} ${unidad || ""} se reparten en partes iguales.`
                  : `${total} ${unidad || ""} repartidos según lo que cada ítem tiene contemplado del material.`}
        </p>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600">Cancelar</button>
          <button type="button" onClick={() => onConfirm(sel)} disabled={sel.length < 1}
            className="bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-xs font-bold"
            data-testid="pm-varios-items-confirmar">
            Imputar a {sel.length || ""} {sel.length === 1 ? "ítem" : "ítems"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Fix AO (2026-09-05): "¿de dónde sale ese 35m? (…) tendría que comparar con ese
// total primeramente. Y también con el total que le corresponde a ese item (…) Y
// también comparar con la cantidad total de los niveles seleccionados" (Edson,
// 04/09). Tres líneas: el material en todo el presupuesto (la que manda), lo de
// esta línea del presupuesto y lo del nivel de esta fila.
function ConsumoBadge({ row, matRow, nivelLabel }) {
  if (!row && !matRow) return null;
  const fmtNum = (v) => Math.round((Number(v) || 0) * 100) / 100;
  const clsDe = (estado) => estado === "excedido" ? "text-red-600" : estado === "por_agotarse" ? "text-amber-600" : "text-emerald-600";
  const delNivel = matRow && nivelLabel ? Number(matRow.por_nivel?.[nivelLabel] || 0) : null;
  return (
    <div className="mt-0.5 space-y-0.5" data-testid="pm-item-consumo-badge">
      {matRow && (
        <p className={`text-[9px] font-semibold ${clsDe(matRow.estado)}`} data-testid="pm-item-consumo-material">
          Material en el Pto: {fmtNum(matRow.presupuestado)} {matRow.unidad} · pedido {fmtNum(matRow.solicitado)} · comprado {fmtNum(matRow.comprado)} ({matRow.pct_comprado}%)
        </p>
      )}
      {row && (
        <p className="text-[9px] text-gray-500" data-testid="pm-item-consumo-item">
          En esta línea del Pto: {fmtNum(row.presupuestado)} · comprado {fmtNum(row.comprado)} ({row.pct_comprado}%)
        </p>
      )}
      {delNivel != null && (
        <p className="text-[9px] text-gray-500" data-testid="pm-item-consumo-nivel">
          En {nivelLabel}: {fmtNum(delNivel)} {matRow.unidad}
        </p>
      )}
    </div>
  );
}

// Fix HY (2026-10-27): "en la visualización del pedido en su detalle, se podría visualizar ítem por ítem en qué
// estado se encuentra ... y si se retiró, que detalle la cantidad retirada" (CEN). Etapa de cada ítem y lo
// retirado / entregado, alimentado de Retiro/Entrega (GET /purchases/requests/{id}/seguimiento-items).
const SEG_COLORES = { sin_oc: "bg-gray-100 text-gray-500 border-gray-200", stock: "bg-teal-100 text-teal-700 border-teal-200" };
const SEG_AYUDA = {
  sin_oc: "No figura en las órdenes de compra de este pedido: no se adjudicó en el análisis o está con otro nombre (ver abajo).",
  stock: "Sale del stock (retiro de stock).",
};
const segCant = (n) => Number(Number(n || 0).toFixed(4));
function SeguimientoCeldas({ f, cant, cargando }) {
  if (!f) {
    return (
      <>
        <td className="py-1.5 pr-3 text-gray-300">{cargando ? "…" : "—"}</td>
        <td className="py-1.5 pr-3 text-center text-gray-300">—</td>
        <td className="py-1.5 pr-3 text-center text-gray-300">—</td>
      </>
    );
  }
  const conRetiro = f.retirado != null && (f.retiros || []).length > 0;
  const q = segCant(cant), ret = segCant(f.retirado), ent = segCant(f.entregado);
  const ayuda = [SEG_AYUDA[f.etapa], (f.ocs || []).join(", "), (f.retiros || []).join(", ")].filter(Boolean).join(" · ");
  return (
    <>
      <td className="py-1.5 pr-3">
        <span className={`inline-block whitespace-nowrap text-[10px] px-2 py-0.5 rounded-full font-semibold border ${STATUS_COLORS[f.etapa] || SEG_COLORES[f.etapa] || "bg-gray-100 text-gray-600 border-gray-200"}`}
          title={ayuda} data-testid="pm-seg-etapa">{f.etapa_label || "—"}</span>
      </td>
      <td className="py-1.5 pr-3 text-center whitespace-nowrap" data-testid="pm-seg-retirado">
        {conRetiro
          ? <span className={`font-semibold ${ret >= q - 0.0001 ? "text-lime-700" : "text-red-600"}`}>{ret}/{q}</span>
          : <span className="text-gray-300">—</span>}
      </td>
      <td className="py-1.5 pr-3 text-center whitespace-nowrap" data-testid="pm-seg-entregado">
        {conRetiro
          ? <span className={`font-semibold ${ret > 0 && ent >= ret - 0.0001 ? "text-emerald-700" : "text-gray-400"}`}>{ent}/{ret}</span>
          : <span className="text-gray-300">—</span>}
      </td>
    </>
  );
}

// Fix AY (2026-09-06): "siempre queda en m" (Edson, 06/09). El presupuesto usa
// su propio vocabulario de unidades (MTS, ML, UN, GLB) y ninguna de esas existe
// en UNITS. Cuando el value de un <select> no coincide con ninguna <option>,
// React marca la primera: por eso todo aparecía en "m" aunque el dato guardado
// fuese "MTS". Se traduce lo que tiene equivalente; lo que no, se conserva tal
// cual y el desplegable lo muestra como una opción más.
const UM_EQUIV = {
  m: "m", mt: "m", mts: "m", ml: "m", "m.": "m", "mts.": "m", metro: "m", metros: "m", "metro lineal": "m",
  m2: "m²", mt2: "m²", "m²": "m²", m3: "m³", mt3: "m³", "m³": "m³",
  kg: "kg", kgs: "kg", kilo: "kg", kilos: "kg",
  un: "un", u: "un", ud: "un", uds: "un", und: "un", unid: "un", unidad: "un", unidades: "un", "c/u": "un", pza: "un", pzas: "un",
  gl: "gl", glb: "gl", gbl: "gl", global: "gl",
  rollo: "rollo", rollos: "rollo", rll: "rollo",
  caja: "caja", cajas: "caja", cja: "caja",
  par: "par", pares: "par",
  juego: "juego", juegos: "juego", jgo: "juego", jgos: "juego",
};

const normalizarUM = (u) => {
  const raw = String(u == null ? "" : u).trim();
  if (!raw) return "un";
  if (UNITS.includes(raw)) return raw;
  return UM_EQUIV[raw.toLowerCase()] || raw;
};

// BLOQUE 63 — fila memoizada: al escribir en el ítem N, React ya NO tiene que
// re-renderizar los demás ítems del pedido (props estables: `item` conserva su
// identidad para las filas no tocadas, `updateItem`/`removeItem` son estables
// vía useCallback en el padre, `suggestions`/`frenteOptions` están memoizados).
// Fix DG (2026-09-16): `setItemsMultiples` reemplaza a `dividirPorItems` y a
// `dividirPorNiveles`; es estable vía useCallback en el padre, así que la
// memoización de la fila sigue funcionando igual que antes. `nivelMap` y
// `pesosItem` también son estables mientras no cambie el presupuesto.
const PMItemRow = React.memo(function PMItemRow({ item, idx, canRemove, suggestions, stockMap, listadoSuggestions, itemsDelPto, nivelOptions, nivelMap, pesosItem, tareaOptions, hasPresupuesto, consumoRow, consumoMatRow, updateItem, updateItemCategoria, updateItemMaterial, removeItem, setItemsMultiples, dragId, hayCopia, copiarItems, pegarItems, tareaCopiada, copiarTarea, pegarTarea }) {
  // Fix BR (2026-09-09): panel de "varios ítems del presupuesto" de esta fila.
  // Fix DG (2026-09-16): el panel de "varios niveles" se fue con la columna Nivel.
  const [variosItems, setVariosItems] = useState(false);
  // Fix DL (2026-09-21): reordenar filas arrastrando.
  const sortable = useSortable({ id: dragId });
  const isListado = item.categoria === "listado";
  // Fix BD (2026-09-07): "materiales" es de la misma naturaleza que "listado"
  // —material que se consume en la obra— pero sin exigir la línea del
  // presupuesto, porque en una obra rápida todavía no existe.
  const esMaterialDeObra = isListado || item.categoria === "materiales";
  // Fix S (2026-09-01): "listado" solo se ofrece si el presupuesto tiene líneas
  // imputables (item_uid) — un presupuesto legacy sin ellas deja el pedido
  // imposible de guardar en esa categoría (hallazgo del testing_agent).
  const listadoDisponible = hasPresupuesto && itemsDelPto.length > 0;
  const categoriaOpts = listadoDisponible ? PM_CATEGORIAS : PM_CATEGORIAS.filter(c => c.value !== "listado");
  // Fix AO (2026-09-05): el aviso mira lo que queda del MATERIAL en el
  // presupuesto (y lo del nivel de la fila, si tiene uno). Antes miraba una sola
  // línea del presupuesto y marcaba excesos que no existían.
  // Fix DG (2026-09-16): "Queremos optimizar el procedimiento de pedido de materiales
  // SUPRIMIENDO la columna de NIVEL, atendiendo a que cada item del presupuesto ya se
  // le asigna un NIVEL, entonces a la hora de solicitar un material, que solamente se
  // seleccione el item del presupuesto" (Edson, 16/09). El nivel deja de elegirse: se
  // lee del ítem al que se imputa la fila. Si la fila está imputada a varios, no hay
  // un nivel único y la celda los lista.
  const uidsFila = Array.isArray(item.item_uids) && item.item_uids.length > 1 ? item.item_uids : null;
  const itemElegido = item.item_uid ? itemsDelPto.find(bi => bi.item_uid === item.item_uid) : null;
  const nivelHeredado = itemElegido && itemElegido.nivel_id ? itemElegido.nivel_id : (item.nivel_id || "");
  const nivelLabel = uidsFila ? null : ((nivelOptions.find(n => n.id === nivelHeredado) || {}).label || null);
  const repartoFila = uidsFila ? repartirPorPesos(item.quantity, uidsFila, pesosItem) : null;
  const disponibleMaterial = consumoMatRow
    ? (nivelLabel && consumoMatRow.por_nivel && consumoMatRow.por_nivel[nivelLabel] != null
        ? Number(consumoMatRow.por_nivel[nivelLabel])
        : Number(consumoMatRow.presupuestado || 0)) - Number(consumoMatRow.comprado || 0)
    : null;
  const overBudget = esMaterialDeObra && (
    disponibleMaterial != null
      ? Number(item.quantity) > disponibleMaterial
      : (consumoRow && Number(item.quantity) > (consumoRow.presupuestado - consumoRow.comprado))
  );
  // Fix G (2026-08-30): si el ítem del presupuesto ya trae su desglose de tareas,
  // el desplegable ofrece primero esas (son las que de verdad le corresponden) y
  // debajo el resto del catálogo. Sin vinculación, las 38 completas.
  const tareasDelItem = (item.item_uid && (tareaOptions.porItem[item.item_uid] || [])) || [];
  // Fix S (2026-09-01): "Ítem del Pto" — el material elegido en Descripción puede
  // venir de una o varias líneas del presupuesto (de_items del fix R). Esas van
  // primero, en su propio optgroup "De este material" (mismo patrón del
  // desplegable de Tarea del fix G, que agrupa "Del ítem" y después "Todas"); el
  // resto de las líneas del presupuesto queda disponible debajo por si el
  // usuario necesita imputar a otra.
  const origenUids = item.material_origen_uids || [];
  const itemsDeEsteMaterial = origenUids.map(uid => itemsDelPto.find(bi => bi.item_uid === uid)).filter(Boolean);
  const itemsRestantes = itemsDelPto.filter(bi => !origenUids.includes(bi.item_uid));
  return (
    <div ref={sortable.setNodeRef} className="grid gap-1 items-start bg-gray-50 rounded-lg p-2" data-testid={`pm-item-row-${idx}`}
      style={{
        gridTemplateColumns: "24px 108px minmax(0,3fr) 56px 64px minmax(0,2fr) 88px 96px 116px minmax(0,1.2fr) minmax(0,1.5fr) 22px",
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        position: sortable.isDragging ? "relative" : undefined,
        zIndex: sortable.isDragging ? 40 : undefined,
        opacity: sortable.isDragging ? 0.8 : undefined,
        boxShadow: sortable.isDragging ? "0 4px 12px rgba(0,0,0,0.15)" : undefined,
      }}>
      <button type="button" {...sortable.attributes} {...sortable.listeners}
        className="flex flex-col items-center gap-0.5 mt-0.5 cursor-grab active:cursor-grabbing"
        style={{ touchAction: "none" }}
        title="Arrastrar para cambiar el orden" aria-label={`Mover la fila ${idx + 1}`}
        data-testid={`pm-item-drag-${idx}`}>
        <span className="text-[11px] font-bold text-[#2E6DA4] bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center">{idx + 1}</span>
        <GripVertical size={12} className="text-gray-400" />
      </button>
      <select value={item.categoria || "menores"} onChange={e => updateItemCategoria(idx, e.target.value)}
        className="border border-gray-300 rounded-lg px-1 py-1.5 text-[11px] bg-white" data-testid={`pm-item-categoria-${idx}`}>
        {categoriaOpts.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      <div>
        {isListado ? (
          <AutocompleteInput
            placeholder="Buscar en el listado de materiales..." value={item.material_name}
            onChange={v => updateItemMaterial(idx, v)}
            suggestions={listadoSuggestions}
            /* Fix G (2026-08-30): el tope por defecto es 8 y en un presupuesto de
               cientos de ítems escondía la mayoría. Con 200 el residente ve la
               lista real; para recorrerla entera está el botón "Ver ítems del
               presupuesto", que abre el listado completo con marcado. */
            maxSuggestions={200}
            multiline   /* Fix IU (2026-11-02): la descripción se ve completa y se sigue editando */
            inputClassName={`w-full border rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-[#2E6DA4]/30 ${!item.item_uid && !uidsFila ? "border-amber-300 bg-amber-50/40" : "border-gray-300"}`}
            data-testid={`pm-item-name-${idx}`}
          />
        ) : (
          <AutocompleteInput
            placeholder="Ej: Cable THW 2.5mm" value={item.material_name}
            onChange={v => updateItemMaterial(idx, v)}
            suggestions={suggestions}
            maxSuggestions={15}
            multiline   /* Fix IU */
            inputClassName="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-[#2E6DA4]/30"
            data-testid={`pm-item-name-${idx}`}
          />
        )}
        {/* Fix FS (2026-10-12): el material elegido está en el stock de la empresa. */}
        {!isListado && stockMap[item.material_name] && (
          <p className="text-[10px] font-semibold text-emerald-700 mt-0.5" data-testid={`pm-item-stock-${idx}`}>
            {stockMap[item.material_name].hint}
          </p>
        )}
        {isListado && !item.item_uid && !uidsFila && <p className="text-[9px] text-amber-600 mt-0.5">Falta imputar — elegí en "Ítem del Pto"</p>}
        {/* Fix BD (2026-09-07): en una obra rápida todavía no hay presupuesto
            contra el cual imputar, así que se avisa que queda para después en vez
            de pedir algo que no se puede hacer. */}
        {item.categoria === "materiales" && !item.item_uid && !uidsFila && (
          <p className="text-[9px] text-gray-400 mt-0.5" data-testid={`pm-item-regulariza-${idx}`}>
            {hasPresupuesto ? 'Opcional: imputalo en "Ítem del Pto"' : "Se imputa cuando la obra tenga su presupuesto"}
          </p>
        )}
        {esMaterialDeObra && item.item_uid && <ConsumoBadge row={consumoRow} matRow={consumoMatRow} nivelLabel={nivelLabel} />}
        {overBudget && <p className="text-[9px] text-red-600 font-bold mt-0.5" data-testid={`pm-item-overbudget-${idx}`}>⚠ Supera lo disponible del presupuesto</p>}
      </div>
      {/* Fix AY (2026-09-06): "debería habilitar la selección de unidad de medida
          o en todo caso estirar bien según el material seleccionado". El campo se
          sigue completando solo al elegir el material, pero ahora se puede
          corregir; y si la unidad del presupuesto no está en la lista, se agrega
          como opción en vez de mostrar la primera (que era lo que hacía aparecer
          todo en "m"). */}
      <select value={item.unit || "un"} onChange={e => updateItem(idx, "unit", e.target.value)}
        className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-xs bg-white" data-testid={`pm-item-unidad-${idx}`}>
        {(item.unit && !UNITS.includes(item.unit) ? [item.unit, ...UNITS] : UNITS).map(u => <option key={u}>{u}</option>)}
      </select>
      <input type="number" min="0" value={item.quantity} onChange={e => updateItem(idx, "quantity", Number(e.target.value))}
        className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs text-center" />
      {/* Fix S (2026-09-01): "se despliega el Pto vinculado y se selecciona a qué
          línea va a ser imputada el costo del material pedido". Se preselecciona
          sola cuando el material viene de una sola línea (updateItemMaterial);
          si viene de varias, o si el usuario prefiere otra, elige aquí. */}
      {/* Fix BR (2026-09-09): la celda pasa a tener el desplegable y, debajo, el
          enlace de multiselección. Va envuelto en un contenedor en columna —igual
          que la celda de Nivel— porque si fueran dos hijos sueltos de la grilla
          se correrían todas las columnas de la derecha. */}
      <div className="flex flex-col gap-0.5">
        {uidsFila ? (
          // Fix DG (2026-09-16): imputada a varios. Antes esto eran N filas en el
          // formulario; ahora es una, y acá se ve a dónde va cada parte.
          <div className="border border-[#2E6DA4]/40 bg-[#EBF3FB]/60 rounded-lg px-1.5 py-1 space-y-0.5"
            data-testid={`pm-item-multi-${idx}`}>
            {uidsFila.map((uid, k) => {
              const bi = itemsDelPto.find(x => x.item_uid === uid);
              return (
                <div key={uid} className="flex items-start gap-1 text-[10px] leading-tight">
                  <span className="flex-1 text-gray-700 whitespace-normal break-words">
                    {bi ? `${bi.numero ? bi.numero + " " : ""}${bi.descripcion}` : uid}
                    <ChipNivel label={nivelDeItem(bi, nivelMap)} />
                  </span>
                  <span className="flex-shrink-0 font-bold text-[#1A4B7A]">{repartoFila[k]}</span>
                </div>
              );
            })}
            <button type="button" onClick={() => setVariosItems(true)}
              className="text-[9px] font-semibold text-[#2E6DA4] hover:underline"
              data-testid={`pm-item-varios-items-${idx}`}>
              Cambiar ítems…
            </button>
          </div>
        ) : (
          <>
            <SelectItemPto
              value={item.item_uid || ""}
              itemsDeEsteMaterial={itemsDeEsteMaterial}
              itemsRestantes={itemsRestantes}
              nivelMap={nivelMap}
              disabled={!hasPresupuesto}
              placeholder={hasPresupuesto ? "— Imputar a —" : "Sin presupuesto"}
              testId={`pm-item-ptoitem-${idx}`}
              onChange={v => {
                updateItem(idx, "item_uid", v || null);
                // Fix AY (2026-09-06): al elegir a qué línea del presupuesto se imputa,
                // se toma la unidad de esa línea, que es el dato bueno. Después se
                // puede seguir corrigiendo a mano.
                const bi = itemsDelPto.find(x => x.item_uid === v);
                if (bi && bi.unidad) updateItem(idx, "unit", normalizarUM(bi.unidad));
                // Fix DG (2026-09-16): y el nivel, que ya no se elige aparte.
                updateItem(idx, "nivel_id", bi && bi.nivel_id ? bi.nivel_id : "");
              }}
            />
            {hasPresupuesto && itemsDelPto.length > 1 && (
              <button type="button" onClick={() => setVariosItems(true)}
                className="text-[9px] font-semibold text-[#2E6DA4] hover:underline text-left"
                data-testid={`pm-item-varios-items-${idx}`}>
                Varios ítems…
              </button>
            )}
          </>
        )}
        {/* Fix DL (2026-09-21): copiar los ítems de esta fila y pegarlos en otra. */}
        {hasPresupuesto && (uidsFila || item.item_uid || hayCopia > 0) && (
          <div className="flex gap-2">
            {(uidsFila || item.item_uid) && (
              <button type="button" onClick={() => copiarItems(uidsFila || [item.item_uid])}
                className="text-[9px] font-semibold text-gray-500 hover:text-[#2E6DA4] hover:underline"
                data-testid={`pm-item-copiar-items-${idx}`}>
                Copiar ítems
              </button>
            )}
            {hayCopia > 0 && (
              <button type="button" onClick={() => pegarItems(idx)}
                className="text-[9px] font-semibold text-emerald-700 hover:underline"
                data-testid={`pm-item-pegar-items-${idx}`}>
                Pegar ítems ({hayCopia})
              </button>
            )}
          </div>
        )}
        {variosItems && (
          <VariosItemsPanel
            itemsDeEsteMaterial={itemsDeEsteMaterial}
            itemsRestantes={itemsRestantes}
            nivelMap={nivelMap}
            pesos={pesosItem}
            cantidad={item.quantity}
            unidad={item.unit}
            seleccion={uidsFila || (item.item_uid ? [item.item_uid] : [])}
            onCancel={() => setVariosItems(false)}
            onConfirm={(uids) => { setVariosItems(false); setItemsMultiples(idx, uids); }}
          />
        )}
      </div>
      <input
        placeholder="Sector" value={item.sector || ""}
        onChange={e => updateItem(idx, "sector", e.target.value)}
        className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-xs"
        data-testid={`pm-item-sector-${idx}`}
      />
      {/* Fix DG (2026-09-16): la columna deja de ser un desplegable. El nivel es el
          del ítem al que se imputa la fila: elegido el ítem, el nivel viene solo. Si
          la fila está imputada a varios ítems, cada uno lleva el suyo y se ven en la
          celda de al lado. */}
      <div className="text-[11px] px-1 py-1.5 whitespace-normal break-words" data-testid={`pm-item-nivel-${idx}`}>
        {uidsFila
          ? <span className="text-gray-400">Según cada ítem</span>
          : nivelLabel
            ? <span className="font-semibold text-[#1A4B7A]">{nivelLabel}</span>
            : <span className="text-gray-300">—</span>}
      </div>
      {/* Fix G (2026-08-30): Tarea y Observación por ítem, ambas opcionales. */}
      {/* Fix DY (2026-09-23): con buscador; mismos grupos y mismo valor. */}
      {/* Fix FH (2026-10-08): "que tenga también la opción equivalente de copiar ítems en
          tareas (copiar tareas)" (CEN). Va en un contenedor para no correr las columnas. */}
      <div className="flex flex-col gap-0.5 min-w-0">
        <SelectBuscable
          value={item.tarea_numero || ""}
          onChange={v => updateItem(idx, "tarea_numero", v)}
          vacio="— Tarea —"
          options={[
            ...tareasDelItem.map(n => ({ value: String(n), label: `${n}. ${tareaOptions.nombres[n] || ""}`, grupo: "Del ítem" })),
            ...tareaOptions.todas.map(t => ({ value: String(t.numero), label: `${t.numero}. ${t.nombre}`, grupo: tareasDelItem.length > 0 ? "Todas" : "Catálogo" })),
          ]}
          className="border border-gray-300 rounded-lg px-1 py-1.5 text-[11px] bg-white min-w-0"
          envolver   /* Fix IU: la tarea elegida se lee completa */
          testId={`pm-item-tarea-${idx}`}
        />
        {(item.tarea_numero || tareaCopiada) && (
          <div className="flex gap-2">
            {item.tarea_numero && (
              <button type="button" onClick={() => copiarTarea(item.tarea_numero)}
                className="text-[9px] font-semibold text-gray-500 hover:text-[#2E6DA4] hover:underline"
                data-testid={`pm-item-copiar-tarea-${idx}`}>
                Copiar tarea
              </button>
            )}
            {tareaCopiada && String(tareaCopiada) !== String(item.tarea_numero || "") && (
              <button type="button" onClick={() => pegarTarea(idx)}
                className="text-[9px] font-semibold text-emerald-700 hover:underline"
                data-testid={`pm-item-pegar-tarea-${idx}`}>
                Pegar tarea ({tareaCopiada})
              </button>
            )}
          </div>
        )}
      </div>
      <input
        placeholder="Marca" value={item.marca_recomendada || ""}
        onChange={e => updateItem(idx, "marca_recomendada", e.target.value)}
        className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-xs"
        data-testid={`pm-item-marca-${idx}`} />
      <input
        placeholder="Observación" value={item.observacion || ""}
        onChange={e => updateItem(idx, "observacion", e.target.value)}
        className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-xs"
        data-testid={`pm-item-observacion-${idx}`}
      />
      {canRemove ? (
        <button type="button" onClick={() => removeItem(idx)} className="flex justify-center text-red-400 hover:text-red-600 mt-1" data-testid={`pm-item-remove-${idx}`}>
          <X size={14} />
        </button>
      ) : <div />}
    </div>
  );
});

export function PedidosMaterialesTab({
  requests, projects, loading, onRefresh, userRole,
  listasClientes, listasObras, allBudgets, refreshBudgets, openNumber,
  filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto,
}) {
  const [showNew, setShowNew] = useState(false);
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  // Fix HY (2026-10-27): etapa y retirado / entregado por ítem del pedido que se abre (se pide al abrirlo).
  const [seguimiento, setSeguimiento] = useState({});
  useEffect(() => {
    if (!expanded) return undefined;
    let vigente = true;
    axios.get(`${API}/purchases/requests/${expanded}/seguimiento-items`, { withCredentials: true })
      .then(r => { if (vigente) setSeguimiento(s => ({ ...s, [expanded]: r.data })); })
      .catch(() => { if (vigente) setSeguimiento(s => ({ ...s, [expanded]: { error: true } })); });
    return () => { vigente = false; };
  }, [expanded]);
  const [viewRequest, setViewRequest] = useState(null);
  const [vincularTarget, setVincularTarget] = useState(null);
  const [form, setForm] = useState(blankPMForm());
  // Fix S (2026-09-01): id del pedido en borrador que se está completando (si
  // el modal se abrió desde "Editar borrador" en el listado). null = pedido nuevo.
  const [editingBorradorId, setEditingBorradorId] = useState(null);
  const [editingStatus, setEditingStatus] = useState("borrador");   // Fix DB (2026-09-16)
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [generatingRSI, setGeneratingRSI] = useState(null); // BLOQUE 24 — pm_id en curso
  const [reversalSuspects, setReversalSuspects] = useState(null); // BLOQUE 63 — aviso de texto invertido al guardar
  // Fix AD (2026-09-03): aviso al cerrar con algo cargado, y respaldo local
  // recuperable al volver a abrir "Nuevo Pedido".
  const { user } = useAuth();
  const [confirmCerrar, setConfirmCerrar] = useState(false);
  const [respaldoLocal, setRespaldoLocal] = useState(null);

  // CEN 2.0 FASE 3 PARTE A/B — listado del presupuesto (sin precios) + niveles +
  // marcas cotizadas, y consumo por ítem, para el presupuesto seleccionado.
  // Fix R (2026-08-31): `materiales` son los materiales explotados desde los
  // componentes del catálogo (ver items-para-pedido en budgets_core.py).
  const [budgetPedidoData, setBudgetPedidoData] = useState({ items: [], niveles: [], marcas_cotizadas: "", marcas_cotizadas_split: null, materiales: [] });
  const [consumoMap, setConsumoMap] = useState({});
  // Fix AO (2026-09-05): consumo POR MATERIAL del listado desglosado — la misma
  // fuente que la pestaña Consumo de Materiales de la obra. El aviso del pedido
  // comparaba contra una sola línea del presupuesto (35 m) cuando el material
  // suma 74,28 m entre todas: un exceso de 3.285% que no era real.
  const [consumoMatMap, setConsumoMatMap] = useState({});
  const budgetListadoMapRef = useRef({});
  // Fix DG (2026-09-16): pesos del reparto por material. Se llena junto con
  // materialMapRef y se lee al guardar y en cada fila.
  const pesosPorMaterialRef = useRef({});
  // Fix R: mapa etiqueta-material → material explotado (con sus de_items), y
  // mapa origen textual ("numero descripcion") → item_uid, para resolver a qué
  // ítem del presupuesto imputar un material que puede venir de varios.
  const materialMapRef = useRef({});
  const origenToItemUidRef = useRef({});

  useEffect(() => {
    if (!form.presupuesto_id) {
      setBudgetPedidoData({ items: [], niveles: [], marcas_cotizadas: "", marcas_cotizadas_split: null, materiales: [] });
      setConsumoMap({});
      setConsumoMatMap({});
      budgetListadoMapRef.current = {};
      materialMapRef.current = {};
      pesosPorMaterialRef.current = {};
      origenToItemUidRef.current = {};
      return;
    }
    axios.get(`${API}/budgets/${form.presupuesto_id}/items-para-pedido`, { withCredentials: true })
      .then(({ data }) => {
        setBudgetPedidoData(data);
        const map = {};
        (data.items || []).forEach(bi => { map[budgetItemLabel(bi)] = bi; });
        budgetListadoMapRef.current = map;
        const omap = {};
        (data.items || []).forEach(bi => { omap[`${bi.numero || ""} ${bi.descripcion || ""}`.trim()] = bi.item_uid; });
        origenToItemUidRef.current = omap;
        const matMap = {};
        const pesosMap = {};
        (data.materiales || []).forEach(m => {
          matMap[materialExplotadoLabel(m)] = m;
          // Fix DG (2026-09-16): {descripción del material: {item_uid: cantidad}} —
          // el peso de cada ítem para repartir lo que se pide de ese material.
          pesosMap[materialExplotadoLabel(m)] = m.por_item_uid || {};
        });
        materialMapRef.current = matMap;
        pesosPorMaterialRef.current = pesosMap;
        // Fix S (2026-09-01): la promoción a "Del Listado de Mat. del Pto" (filas
        // vacías en "menores") se hace acá, UNA SOLA VEZ por presupuesto elegido,
        // y SOLO si el presupuesto realmente tiene líneas imputables (item_uid).
        // Hallazgo del testing_agent: presupuestos legacy devuelven items=[] con
        // materiales=[...] (explosión sin línea de origen imputable) — promover
        // en ese caso dejaba la fila en una categoría que el guardado siempre
        // rechaza, sin salida visible para el usuario.
        if ((data.items || []).length > 0) {
          setForm(f => f.items.some(i => i.categoria === "menores" && !(i.material_name || "").trim())
            ? { ...f, items: f.items.map(i => (i.categoria === "menores" && !(i.material_name || "").trim()) ? { ...i, categoria: "listado", item_uid: null } : i) }
            : f);
        }
      })
      .catch(() => {});
    axios.get(`${API}/budgets/${form.presupuesto_id}/consumo`, { withCredentials: true })
      .then(({ data }) => {
        const m = {};
        (data || []).forEach(row => { m[row.item_uid] = row; });
        setConsumoMap(m);
      })
      .catch(() => {});
    // Fix AO (2026-09-05): consumo por material (mismo endpoint que la obra).
    axios.get(`${API}/budgets/${form.presupuesto_id}/consumo-materiales`, { withCredentials: true })
      .then(({ data }) => {
        const m = {};
        (data?.materiales || []).forEach(row => {
          if (row.tipo === "material") m[`${row.descripcion} (${row.unidad})`] = row;
        });
        setConsumoMatMap(m);
      })
      .catch(() => {});
  }, [form.presupuesto_id]);

  // Fix G (2026-08-30) — corrección detectada al probar el pedido SIN presupuesto:
  // el ítem nuevo nace con categoria="listado" (default del Fix A), pero cuando no
  // hay presupuesto PMItemRow filtra esa opción del desplegable, así que el select
  // mostraba "Material menor" mientras el estado seguía en "listado" → fila virgen
  // con el aviso ámbar y guardado frenado. Se normaliza el estado a "menores" para
  // que coincida con lo que el desplegable ofrece. No cambia ninguna regla: con
  // presupuesto vinculado, "listado" sigue disponible y sigue exigiendo item_uid.
  // Fix BD (2026-09-07): sin presupuesto la fila pasa a "Materiales de Obra", no
  // a "Materiales Menores". Un material de obra cargado como menor queda
  // contabilizado en otro concepto y después no hay forma de reconocerlo para
  // imputarlo cuando la obra reciba su cuenta analítica.
  useEffect(() => {
    if (form.presupuesto_id) return;
    setForm(f => f.items.some(i => i.categoria === "listado")
      ? { ...f, items: f.items.map(i => i.categoria === "listado" ? { ...i, categoria: "materiales", item_uid: null } : i) }
      : f);
  }, [form.presupuesto_id, form.items]);

  // Fix S (2026-09-01): el efecto del fix H se ELIMINA. Dependía de `form.items`,
  // así que corría en cada cambio de cualquier fila: si el usuario elegía
  // "Material menor" en una fila todavía sin descripción, se la devolvía a "Del
  // presupuesto" al instante y la selección era imposible de sostener.
  // Reportado por el cliente: "no me deja seleccionar material menor".
  // La promoción al valor por defecto ahora ocurre UNA SOLA VEZ, al elegir el
  // presupuesto (ver el onChange del select de Presupuesto Objetivo), y después
  // el usuario decide sin interferencia.

  // Fix S (2026-09-01): la categoría "Del Listado de Mat. del Pto" ahora
  // despliega SOLO el listado de materiales explotados —para tener una
  // referencia de lo ya consumido de ese listado, según pidió Sergio— y no las
  // líneas del presupuesto. Elegir a qué línea imputar el material pasa a la
  // columna nueva "Ítem del Pto" (ver PMItemRow). En un presupuesto sin bocas
  // vinculadas, el fix R igual devuelve una línea por ítem, así que la lista
  // nunca queda vacía.
  const listadoSuggestions = useMemo(
    () => (budgetPedidoData.materiales || []).map(materialExplotadoLabel),
    [budgetPedidoData.materiales]
  );

  // Fix G (2026-08-30): catálogo de las 38 tareas + el desglose propio de cada
  // ítem del presupuesto, para el desplegable Tarea (opcional) de cada fila.
  const [catalogoTareas, setCatalogoTareas] = useState([]);
  useEffect(() => {
    axios.get(`${API}/catalogo-tareas`, { withCredentials: true })
      .then(({ data }) => setCatalogoTareas(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);
  const tareaOptions = useMemo(() => {
    const nombres = {};
    catalogoTareas.forEach(t => { nombres[t.numero] = t.nombre; });
    const porItem = {};
    (budgetPedidoData.items || []).forEach(bi => {
      const nums = (bi.tareas || []).map(t => t.tarea_numero).filter(n => n != null);
      if (nums.length > 0) porItem[bi.item_uid] = nums;
    });
    return { todas: catalogoTareas, nombres, porItem };
  }, [catalogoTareas, budgetPedidoData.items]);

  // Fix G (2026-08-30): listado completo del presupuesto con marcado múltiple.
  // El buscador por texto solo sirve si ya se sabe qué se busca; el documento
  // del cliente del 29/08 pide poder desplegar TODOS los ítems y marcar.
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerMarked, setPickerMarked] = useState({});
  const pickerItems = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const all = budgetPedidoData.items || [];
    if (!q) return all;
    return all.filter(bi => `${bi.numero || ""} ${bi.descripcion || ""}`.toLowerCase().includes(q));
  }, [budgetPedidoData.items, pickerSearch]);
  const pickerCount = useMemo(() => Object.values(pickerMarked).filter(Boolean).length, [pickerMarked]);
  const openItemPicker = () => { setPickerSearch(""); setPickerMarked({}); setShowItemPicker(true); };
  const confirmItemPicker = () => {
    const elegidos = (budgetPedidoData.items || []).filter(bi => pickerMarked[bi.item_uid]);
    if (elegidos.length === 0) { setShowItemPicker(false); return; }
    setForm(f => {
      const vacias = f.items.filter(i => !i.material_name.trim());
      const llenas = f.items.filter(i => i.material_name.trim());
      const nuevas = elegidos.map(bi => ({
        ...newPMItem(),
        categoria: "listado",
        item_uid: bi.item_uid,
        material_name: budgetItemLabel(bi),
        unit: bi.unidad || "un",
        nivel_id: bi.nivel_id || "",
      }));
      const items = [...llenas, ...nuevas];
      return { ...f, items: items.length > 0 ? items : vacias };
    });
    setShowItemPicker(false);
    toast.success(`${elegidos.length} ítem${elegidos.length !== 1 ? "s" : ""} del presupuesto agregado${elegidos.length !== 1 ? "s" : ""}`);
  };

  // Listas catalog data for PM form (listasClientes/listasObras/allBudgets ahora
  // vienen como props desde Purchases.jsx — BLOQUE 27, compartidos con el resto de
  // las pestañas). listaMateriales/listaFrentes siguen locales (solo usados en el form).
  const [listaMateriales, setListaMateriales] = useState([]);
  const [listaFrentes, setListaFrentes] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);

  // BLOQUE 63 — memoizado: antes se recalculaba en CADA render del formulario
  // (cada tecla de cualquier ítem), recorriendo todo el catálogo sin necesidad.
  const materialSuggestions = useMemo(
    () => listaMateriales.map(m => m.descripcion || m.description || "").filter(Boolean),
    [listaMateriales]
  );
  // Fix FS (2026-10-12): "al escribir en Descripción se debe filtrar por materiales en stock,
  // desplegando el mismo desplegable que en Registrar Entrada (código – nombre · marca),
  // indicando que está disponible en stock con la cantidad correspondiente" (Sergio). Mismo
  // texto y mismo orden que Inventario → Registrar Entrada; solo los que tienen stock disponible.
  const [stockMateriales, setStockMateriales] = useState([]);
  useEffect(() => {
    if (!showNew) return;
    axios.get(`${API}/inventory/materials`, { withCredentials: true })
      .then(r => setStockMateriales(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [showNew]);
  const stockInfo = useMemo(() => {
    const map = {};
    // Fix GF (2026-10-15): "las cantidades del depósito central se toman para las adjudicaciones desde
    // los pedidos y análisis" (CEN). Lo disponible es lo del Almacén Central (físico menos reservado),
    // igual que en el Análisis; lo que está en otras obras o almacenes no cuenta.
    const lista = [...stockMateriales]
      .filter(m => disponibleCentral(m) > 0)
      .sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), "es", { numeric: true, sensitivity: "base" }))
      .map(m => {
        const label = `${m.code} – ${m.name}${m.marca ? ` · ${m.marca}` : ""}`;
        const hint = `Disponible en Almacén Central: ${disponibleCentral(m).toLocaleString("es-PY", { maximumFractionDigits: 2 })} ${m.unit || ""}`.trim();
        map[label] = { unit: m.unit, hint };
        return { value: label, label, hint };
      });
    return { lista, map };
  }, [stockMateriales]);
  const stockMapRef = useRef({});
  stockMapRef.current = stockInfo.map;
  const sugerenciasConStock = useMemo(() => [...stockInfo.lista, ...materialSuggestions], [stockInfo, materialSuggestions]);
  const frenteOptionsFlat = useMemo(
    () => listaFrentes.flatMap(f => (f.frentes || []).map(fr => `${f.cod}. ${fr}`)),
    [listaFrentes]
  );

  // Fix Z (2026-09-02): órdenes de trabajo abiertas (asistencias) para imputar el pedido.
  const [otAbiertas, setOtAbiertas] = useState([]);
  useEffect(() => {
    axios.get(`${API}/listas/materiales`).then(r => setListaMateriales(r.data || [])).catch(() => {});
    axios.get(`${API}/listas/frentes`).then(r => setListaFrentes(r.data || [])).catch(() => {});
    axios.get(`${API}/asistencias/abiertas`, { withCredentials: true }).then(r => setOtAbiertas(r.data || [])).catch(() => {});
  }, []);

  // Fix AD (2026-09-03): respaldo automático del pedido nuevo mientras se carga.
  // Con 800 ms de retardo para no escribir en cada tecla. No aplica al completar
  // un borrador que ya existe en el sistema: ahí la fuente de verdad es el borrador.
  useEffect(() => {
    if (!showNew || editingBorradorId) return;
    if (!formTieneContenido(form)) return;
    const t = setTimeout(() => guardarRespaldoLocal(form, user?._id), 800);
    return () => clearTimeout(t);
  }, [showNew, editingBorradorId, form, user?._id]);

  // BLOQUE 34 — deep-link desde el Dashboard: abre directamente el detalle
  // del pedido clickeado (?open=<numero>) en cuanto llega la data.
  const openedRef = useRef(false);
  useEffect(() => {
    if (!openNumber || openedRef.current || !requests.length) return;
    const target = requests.find(r => r.number === openNumber);
    if (target) {
      setViewRequest(target);
      openedRef.current = true;
    }
  }, [openNumber, requests]);

  // Load budgets when form opens (refresca el catálogo compartido del padre)
  useEffect(() => {
    if (!showNew) return;
    setLoadingBudgets(true);
    refreshBudgets();
    setLoadingBudgets(false);
  }, [showNew, refreshBudgets]);

  // Filter obras by selected cliente
  const obrasFiltradas = form.lista_cliente_id
    ? listasObras.filter(o => o.cliente_id === form.lista_cliente_id || o._id === form.lista_obra_id)
    : listasObras;

  // BLOQUE 1 PARTE C: dropdown "Presupuesto Objetivo" muestra SOLO aprobados con título completo.
  const selectedObra = listasObras.find(o => o._id === form.lista_obra_id);
  // Fix X (2026-09-02): con un presupuesto de tipo Varios (asistencias técnicas,
  // trabajos esporádicos) la obra es opcional: el pedido queda imputado al
  // presupuesto. `tipo` viene en el listado liviano de presupuestos.
  const presupuestoEsVarios = ((allBudgets.find(b => (b._id || b.id) === form.presupuesto_id) || {}).tipo || "obra") === "varios";
  // Fix DY (2026-09-23): con cliente elegido, solo sus presupuestos.
  const presupuestosFiltrados = filtrarPresupuestos(allBudgets, { clienteId: form.lista_cliente_id, obraSel: selectedObra, listasClientes, listasObras });

  // BLOQUE 40 PARTE B — el Operario Electricista crea su propio pedido de materiales
  // (su sandbox del Bloque 31 ya permite POST /purchases/requests + import-items).
  const canCreate = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "Compras", "Residente de Obra", "Operario Electricista"]);
  const canDelete = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Compras", "Jefe de Compras"]);
  // BLOQUE 16 — vincular retroactivamente: sólo roles de gestión
  const canLink = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Jefe de Compras", "Coordinador de Obra"]);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, newPMItem()] }));
  // Fix BR (2026-09-09): `dividirPorItems` necesita los ítems del presupuesto para
  // resolver la unidad de cada línea nueva, pero tiene que seguir siendo un
  // useCallback SIN dependencias para no romper la memoización de las filas
  // (BLOQUE 63). La ref se actualiza en cada render y el callback la lee cuando
  // se ejecuta, que es cuando el dato ya está cargado.
  const budgetPedidoDataRef = useRef(budgetPedidoData);
  budgetPedidoDataRef.current = budgetPedidoData;
  // Fix DG (2026-09-16): {nivel_id: label}, para mostrar el nivel al lado de cada ítem.
  const nivelMap = useMemo(() => {
    const m = {};
    (budgetPedidoData.niveles || []).forEach(n => { m[n.id] = n.label; });
    return m;
  }, [budgetPedidoData.niveles]);

  const removeItem = useCallback((idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) })), []);
  const updateItem = useCallback((idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
  }, []);

  // Fix BR (2026-09-09): pedir el mismo material para varios ítems del presupuesto.
  // Fix DG (2026-09-16): "debe ser multi seleccion, no que cree otra fila como ahora
  // mismo" (Edson, 16/09). Antes esto reemplazaba la fila por N filas en el
  // formulario; ahora la fila queda una sola con los ítems marcados guardados en
  // `item_uids`, y la apertura a N líneas pasa al momento de guardar (expandirFilas).
  // Con un solo ítem marcado vuelve al caso normal de siempre: `item_uid` a secas.
  const setItemsMultiples = useCallback((idx, uids) => {
    if (!Array.isArray(uids)) return;
    const limpios = uids.filter(Boolean);
    setForm(f => {
      const items = [...f.items];
      const base = items[idx];
      if (!base) return f;
      const delPto = budgetPedidoDataRef.current.items || [];
      if (limpios.length < 2) {
        const uid = limpios[0] || null;
        const bi = uid ? delPto.find(x => x.item_uid === uid) : null;
        items[idx] = {
          ...base, item_uids: null, item_uid: uid,
          nivel_id: bi && bi.nivel_id ? bi.nivel_id : "",
          unit: bi && bi.unidad ? normalizarUM(bi.unidad) : base.unit,
        };
      } else {
        items[idx] = { ...base, item_uids: limpios, item_uid: null, nivel_id: "" };
      }
      return { ...f, items };
    });
    if (limpios.length >= 2) toast.success(`Imputado a ${limpios.length} ítems del presupuesto`);
    else if (limpios.length === 1) toast.success("Imputado a 1 ítem del presupuesto");   // Fix FH
  }, []);
  // Fix DL (2026-09-21): copiar la selección de ítems del Pto de una fila y pegarla
  // en otras. Pegar cambia solo la imputación (y el nivel que viene del ítem); no
  // toca material, unidad ni cantidad. Cambiar de presupuesto vacía lo copiado.
  const [itemsCopiados, setItemsCopiados] = useState(null);
  const itemsCopiadosRef = useRef(null);
  itemsCopiadosRef.current = itemsCopiados;
  useEffect(() => { setItemsCopiados(null); }, [form.presupuesto_id]);
  const copiarItems = useCallback((uids) => {
    const limpios = (uids || []).filter(Boolean);
    if (!limpios.length) return;
    setItemsCopiados(limpios);
    toast.success(`${limpios.length === 1 ? "Ítem copiado" : `${limpios.length} ítems copiados`}. Usá "Pegar ítems" en otra fila.`);
  }, []);
  const pegarItems = useCallback((idx) => {
    const delPto = budgetPedidoDataRef.current.items || [];
    const uids = (itemsCopiadosRef.current || []).filter(u => delPto.some(bi => bi.item_uid === u));
    if (!uids.length) return;
    setForm(f => {
      const items = [...f.items];
      const base = items[idx];
      if (!base) return f;
      if (uids.length === 1) {
        const bi = delPto.find(x => x.item_uid === uids[0]);
        items[idx] = { ...base, item_uids: null, item_uid: uids[0], nivel_id: bi && bi.nivel_id ? bi.nivel_id : "" };
      } else {
        items[idx] = { ...base, item_uids: [...uids], item_uid: null, nivel_id: "" };
      }
      return { ...f, items };
    });
  }, []);
  // Fix FH (2026-10-08): copiar la tarea de una fila y pegarla en otras.
  const [tareaCopiada, setTareaCopiada] = useState("");
  const tareaCopiadaRef = useRef("");
  tareaCopiadaRef.current = tareaCopiada;
  const copiarTarea = useCallback((num) => {
    if (!num) return;
    setTareaCopiada(String(num));
    toast.success(`Tarea ${num} copiada. Usá "Pegar tarea" en otra fila.`);
  }, []);
  const pegarTarea = useCallback((idx) => {
    const num = tareaCopiadaRef.current;
    if (!num) return;
    setForm(f => {
      const items = [...f.items];
      if (!items[idx]) return f;
      items[idx] = { ...items[idx], tarea_numero: num };
      return { ...f, items };
    });
  }, []);
  // Fix DL (2026-09-21): reordenar filas arrastrando el número de la fila.
  const pmSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const pmIdsClave = form.items.map(pmRowId).join("|");
  const pmSortIds = useMemo(() => pmIdsClave.split("|"), [pmIdsClave]);
  const handleDragEndPM = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    setForm(f => {
      const items = moverFilaPM(f.items, active.id, over.id);
      return items === f.items ? f : { ...f, items };
    });
  }, []);
  // CEN 2.0 FASE 3 PARTE A — cambiar categoría resetea la vinculación al listado
  // (item_uid) para no dejar un ítem "listado" apuntando a otra descripción.
  const updateItemCategoria = useCallback((idx, categoria) => {
    setForm(f => {
      const items = [...f.items];
      const cur = items[idx];
      items[idx] = categoria === "listado"
        ? { ...cur, categoria, material_name: "", item_uid: null, material_origen_uids: null }
        : { ...cur, categoria, item_uid: null, material_origen_uids: null };
      return { ...f, items };
    });
  }, []);
  // Fix S (2026-09-01): la categoría "listado" ahora resuelve contra el listado
  // de materiales explotados (fix R) exclusivamente — `listadoSuggestions` ya no
  // ofrece líneas del presupuesto. `material_origen_uids` guarda de qué línea(s)
  // viene el material elegido, para que la columna "Ítem del Pto" (PMItemRow)
  // los muestre agrupados primero ("De este material"). Si viene de una sola,
  // se preselecciona sola; si viene de varias, o de ninguna, el usuario elige en
  // esa columna. Si el texto no matchea ningún material (texto libre), no hay
  // origen y el usuario imputa a mano.
  const updateItemMaterial = useCallback((idx, value) => {
    setForm(f => {
      const items = [...f.items];
      const cur = items[idx];
      if (cur.categoria === "listado") {
        const mat = materialMapRef.current[value];
        if (mat) {
          const uids = Array.from(new Set((mat.de_items || []).map(o => origenToItemUidRef.current[o]).filter(Boolean)));
          // Fix AY (2026-09-06): MTS → m, UN → un. Antes entraba tal cual y el
          // desplegable, al no encontrar la opción, mostraba "m".
          items[idx] = { ...cur, material_name: value, unit: normalizarUM(mat.unidad), material_origen_uids: uids, item_uid: uids.length === 1 ? uids[0] : null };
        } else {
          const bi = budgetListadoMapRef.current[value];
          items[idx] = bi
            ? { ...cur, material_name: value, item_uid: bi.item_uid, unit: normalizarUM(bi.unidad), material_origen_uids: [bi.item_uid] }
            : { ...cur, material_name: value, item_uid: null, material_origen_uids: null };
        }
      } else {
        const enStock = stockMapRef.current[value];   // Fix FS: al elegir un material del stock se completa su unidad
        items[idx] = enStock && enStock.unit ? { ...cur, material_name: value, unit: normalizarUM(enStock.unit) } : { ...cur, material_name: value };
      }
      return { ...f, items };
    });
  }, []);

  const handleImportedItems = (importedItems) => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items.filter(i => i.material_name.trim()), ...importedItems.map(it => ({ categoria: "menores", item_uid: null, nivel_id: "", ...it, __uid: it.__uid || `pm-imp-${Date.now()}-${++__pmItemSeq}` }))],
    }));
    setShowImport(false);
    toast.success(`${importedItems.length} ítem${importedItems.length !== 1 ? "s" : ""} agregado${importedItems.length !== 1 ? "s" : ""} al pedido`);
  };

  // BLOQUE 63 — antes de guardar, avisa (nunca corrige solo) si alguna
  // descripción tal como está escrita NO existe en el catálogo pero su reverso
  // exacto sí — señal fuerte de texto invertido por el bug de Android/IME.
  const detectReversedItems = () => {
    const catalog = listaMateriales.map(m => (m.descripcion || m.description || "").trim()).filter(Boolean);
    const catalogLower = new Set(catalog.map(c => c.toLowerCase()));
    const suspects = [];
    form.items.forEach((it, idx) => {
      const text = (it.material_name || "").trim();
      if (!text) return;
      const reversed = text.split("").reverse().join("");
      if (reversed.toLowerCase() === text.toLowerCase()) return; // palíndromo, nada que sugerir
      if (catalogLower.has(text.toLowerCase())) return; // el texto tal cual ya es válido
      if (!catalogLower.has(reversed.toLowerCase())) return; // el reverso tampoco está en el catálogo
      const proposed = catalog.find(c => c.toLowerCase() === reversed.toLowerCase()) || reversed;
      suspects.push({ idx, current: text, proposed, accept: false });
    });
    return suspects;
  };

  // Fix S (2026-09-01): si se está completando un borrador (editingBorradorId),
  // primero se guardan los últimos cambios y recién ahí se confirma (pasa a
  // "pendiente" y sigue el flujo de siempre). Si falta algo, el backend devuelve
  // 400 y el pedido queda intacto en borrador — se puede reabrir y reintentar.
  const doSubmit = async (items) => {
    setSaving(true);
    try {
      // Fix O (2026-08-31): solo se manda si el usuario cargó un número; vacío se
      // omite por completo (nunca se manda como "" — el backend espera un float).
      const { dias_uso_estimado, ...formRest } = form;
      // Fix DG (2026-09-16): acá se abren las filas imputadas a varios ítems, con la
      // cantidad ya repartida. De acá para abajo el pedido es igual que siempre: una
      // línea por ítem, que es lo que leen la aprobación, la OC y el consumo.
      const payload = {
        ...formRest,
        items: stripUid(expandirFilas(items, pesosPorMaterialRef.current, budgetPedidoDataRef.current.items)),
        ...(dias_uso_estimado ? { dias_uso_estimado: Number(dias_uso_estimado) } : {}),
      };
      if (editingBorradorId) {
        await axios.put(`${API}/purchases/requests/${editingBorradorId}/borrador`, payload, { withCredentials: true });
        await axios.put(`${API}/purchases/requests/${editingBorradorId}/confirmar`, {}, { withCredentials: true });
        toast.success("Pedido confirmado");
      } else {
        await axios.post(`${API}/purchases/requests`, payload, { withCredentials: true });
        toast.success("Pedido de materiales creado");
      }
      setShowNew(false);
      setForm(blankPMForm());
      setEditingBorradorId(null);
      setReversalSuspects(null);
      // Fix AD (2026-09-03): el pedido quedó guardado en el sistema; el respaldo
      // local pierde sentido y no debe ofrecerse la próxima vez.
      setRespaldoLocal(null);
      borrarRespaldoLocal();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear pedido"); }
    finally { setSaving(false); }
  };

  // Fix S (2026-09-01): "Guardar borrador" — sin ninguna de las validaciones de
  // handleSave, porque un borrador puede quedar incompleto a propósito.
  const handleSaveBorrador = async () => {
    setSaving(true);
    try {
      const { dias_uso_estimado, ...formRest } = form;
      // Fix DG (2026-09-16): mismo reparto que en handleSave.
      const items = stripUid(expandirFilas(form.items, pesosPorMaterialRef.current, budgetPedidoDataRef.current.items));
      const diasUsoField = dias_uso_estimado ? { dias_uso_estimado: Number(dias_uso_estimado) } : {};
      if (editingBorradorId) {
        // PUT /borrador no lleva guardar_borrador — es un flag exclusivo del alta.
        await axios.put(`${API}/purchases/requests/${editingBorradorId}/borrador`, { ...formRest, items, ...diasUsoField }, { withCredentials: true });
        // Fix DB (2026-09-16): en un pedido devuelto no se guardó "un borrador"; se
        // guardaron las correcciones, y sigue esperando que lo reenvíen.
        toast.success(editingStatus === "modificar"
          ? "Correcciones guardadas — todavía falta reenviarlo para aprobación"
          : "Borrador actualizado");
      } else {
        await axios.post(`${API}/purchases/requests`, { ...formRest, items, guardar_borrador: true, ...diasUsoField }, { withCredentials: true });
        toast.success("Pedido guardado como borrador");
      }
      setShowNew(false);
      setForm(blankPMForm());
      setEditingBorradorId(null);
      setEditingStatus("borrador");   // Fix DB (2026-09-16)
      // Fix AD (2026-09-03): ver el comentario del CAMBIO 5.
      setConfirmCerrar(false);
      setRespaldoLocal(null);
      borrarRespaldoLocal();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar el borrador"); }
    finally { setSaving(false); }
  };

  // Fix S (2026-09-01): reabre un borrador en el mismo modal, precargado, para
  // que Edson pueda completarlo cuando vuelva de la obra.
  const openBorradorForEdit = (r) => {
    setEditingBorradorId(r._id);
    // Fix DB (2026-09-16): el mismo formulario sirve para completar un borrador y para
    // corregir un pedido devuelto; los carteles cambian según cuál sea.
    setEditingStatus(r.status || "borrador");
    setForm({
      dias_uso_estimado: r.dias_uso_estimado != null ? String(r.dias_uso_estimado) : "",
      urgency: r.urgency || "normal",
      target_date: r.target_date || "",
      notes: r.notes || "",
      lista_cliente_id: r.lista_cliente_id || "",
      lista_obra_id: r.lista_obra_id || "",
      pto_objetivo: r.pto_objetivo || "",
      presupuesto_id: r.presupuesto_id || "",
      presupuesto_codigo: r.presupuesto_codigo || "",
      obra_rapida: !!r.obra_rapida,
      obra_rapida_nombre: r.obra_rapida_nombre || "",
      asistencia_id: r.asistencia_id || "", asistencia_number: r.asistencia_number || "",
      items: (r.items && r.items.length > 0 ? r.items : [{}]).map(it => ({
        categoria: "menores", item_uid: null, material_origen_uids: null, material_name: "",
        quantity: 1, unit: "m", marca_recomendada: "", sector: "", nivel_id: "", tarea_numero: "", observacion: "",
        ...it, __uid: `pm-borrador-${Date.now()}-${++__pmItemSeq}`,
        // Fix S: aproximación al reabrir — el material puede venir de varias
        // líneas, pero la única que sabemos con certeza es la ya imputada. Se
        // muestra bajo "De este material" en la columna; el usuario puede
        // elegir otra igual desde "Todas las líneas del presupuesto".
        material_origen_uids: it.item_uid ? [it.item_uid] : null,
      })),
    });
    setShowNew(true);
  };

  // Fix S (2026-09-01): cerrar/cancelar siempre limpia el modo edición de
  // borrador, para que "Nuevo Pedido" no reabra por error el formulario anterior.
  // Fix AD (2026-09-03): descartar es ahora una acción explícita — la usa el botón
  // "Salir sin guardar" del aviso, no el clic al costado de la ventana.
  const descartarYCerrar = () => {
    setConfirmCerrar(false);
    setShowNew(false);
    setEditingBorradorId(null);
    setEditingStatus("borrador");   // Fix DB (2026-09-16)
    setForm(blankPMForm());
    setRespaldoLocal(null);
    borrarRespaldoLocal();
  };

  // Fix AD (2026-09-03): el clic en el fondo, la X y la tecla Escape pasan todos
  // por acá (el Modal compartido llama a onClose en los tres casos). Si hay algo
  // cargado, se pregunta antes de perderlo.
  // Fix AD (retest testing_agent, 2026-09-03): con un modal hijo abierto
  // (Importar Excel/PDF, picker de ítems, aviso de texto invertido, o el propio
  // aviso de cierre) Escape dispara el keydown listener de AMBOS modales — sin
  // este guardia, el aviso de cierre se abría encima del modal hijo que seguía
  // abierto. El modal hijo atiende su propio Escape con su propio onClose.
  const closeNewModal = () => {
    if (saving) return;
    if (showImport || showItemPicker || reversalSuspects || confirmCerrar) return;
    if (formTieneContenido(form)) { setConfirmCerrar(true); return; }
    descartarYCerrar();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.obra_rapida) {
      if (!form.obra_rapida_nombre.trim()) { toast.error("Ingresá el nombre de la obra para el pedido de obra rápida"); return; }
    } else if (!form.lista_obra_id && !presupuestoEsVarios && !form.asistencia_id) {
      // Fix G (2026-08-30): el presupuesto pasa a ser opcional ("en lo posible
      // se vincula a pto"). Lo obligatorio ahora es la obra, para que ningún
      // pedido quede sin imputar.
      // Fix X (2026-09-02): salvo con un presupuesto de tipo Varios vinculado.
      toast.error("Elegí la obra del pedido (o vinculá un presupuesto de tipo Varios)"); return;
    }
    if (!form.items.some(i => i.material_name)) { toast.error("Agregue al menos un material"); return; }
    const filledItems = form.items.filter(i => i.material_name);
    const missingCategoria = filledItems.some(i => !i.categoria);
    if (missingCategoria) { toast.error("Todos los ítems deben tener una categoría"); return; }
    // Fix A (2026-08-27): el mensaje anterior no decía QUÉ fila estaba sin
    // vincular, así que el residente no sabía qué corregir y atribuía el
    // bloqueo al campo Nivel (que es opcional). Ahora se nombran las filas.
    // Además, si el presupuesto no trae ítems disponibles para pedido, el
    // aviso lo dice explícitamente en vez de dejarlo adivinando.
    const filasSinVincular = form.items
      .map((i, idx) => ({ i, nro: idx + 1 }))
      .filter(({ i }) => i.material_name && i.categoria === "listado" && !i.item_uid && !(Array.isArray(i.item_uids) && i.item_uids.length > 1))
      .map(({ nro }) => nro);
    if (filasSinVincular.length > 0) {
      const listadoVacio = !(budgetPedidoData.items || []).length;
      toast.error(
        listadoVacio
          ? "El presupuesto seleccionado no tiene ítems disponibles para pedido. Avisá a administración, o cargá el material con la categoría 'Materiales Menores', 'Imprevisto' o 'Equipamiento'."
          : `Elegí el ítem del presupuesto en la fila ${filasSinVincular.join(", ")}, o cambiá su categoría.`
      );
      return;
    }
    const suspects = detectReversedItems();
    if (suspects.length > 0) { setReversalSuspects(suspects); return; }
    await doSubmit(form.items);
  };

  // BLOQUE 63 — el usuario decidió (aceptar/dejar) cada sugerencia del aviso.
  const confirmReversalAndSave = () => {
    const items = [...form.items];
    (reversalSuspects || []).forEach(s => {
      if (s.accept) items[s.idx] = { ...items[s.idx], material_name: s.proposed };
    });
    setForm(f => ({ ...f, items }));
    doSubmit(items);
  };

  const handleDeleteRequest = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchases/requests/${deleteTarget._id}`, { withCredentials: true });
      toast.success(`Pedido ${deleteTarget.number} eliminado`);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar pedido"); }
    finally { setDeleting(false); }
  };

  // BLOQUE 24 — genera (o reimprime, idempotente en backend) el documento de aval
  // de Retiro de Stock Interno para un PM cubierto 100% por stock (sin PP generado).
  const handleGenerateRSI = async (pmId) => {
    setGeneratingRSI(pmId);
    try {
      const res = await axios.post(`${API}/purchases/stock-withdrawals`, { pm_id: pmId }, { withCredentials: true });
      generateRetiroStockPDF(res.data);
      toast.success(`Solicitud de retiro de stock interno: ${res.data.number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar la solicitud de retiro");
    } finally {
      setGeneratingRSI(null);
    }
  };

  // Fix S (2026-09-01): confirmación rápida desde la tarjeta, sin abrir el
  // formulario — si al backend le falta algo (categoría/ítem/obra), el 400
  // avisa qué falta y el pedido sigue en borrador para reabrirlo con "Editar".
  const [confirmingBorrador, setConfirmingBorrador] = useState(null);
  const handleConfirmBorrador = async (r) => {
    setConfirmingBorrador(r._id);
    try {
      await axios.put(`${API}/purchases/requests/${r._id}/confirmar`, {}, { withCredentials: true });
      toast.success(`Pedido ${r.number} confirmado`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Faltan datos para confirmar — abrí \"Editar\" para completarlo");
    } finally {
      setConfirmingBorrador(null);
    }
  };

  const URGENCY_OPTS = { normal: { label: "Normal", cls: "bg-gray-100 text-gray-700" }, alta: { label: "Alta", cls: "bg-orange-100 text-orange-700" }, critica: { label: "Crítico", cls: "bg-red-100 text-red-700" } };

  const filteredUnsorted = requests.filter(r => {
    const matchStatus = filterStatus === "todos" || r.status === filterStatus;
    const matchCliente = !filterCliente || r.lista_cliente_id === filterCliente;
    const matchObra = !filterObra || r.lista_obra_id === filterObra;
    let matchPresupuesto = true;
    if (filterPresupuesto === OBRA_RAPIDA_FILTER) matchPresupuesto = !!r.obra_rapida;
    else if (filterPresupuesto) matchPresupuesto = r.presupuesto_id === filterPresupuesto;
    const matchUrgency = !filterUrgency || r.urgency === filterUrgency;
    const matchSearch = !search || (r.number || "").toLowerCase().includes(search.toLowerCase()) || (r.project_name || "").toLowerCase().includes(search.toLowerCase()) || (r.obra_rapida_nombre || "").toLowerCase().includes(search.toLowerCase()) || (r.material_type || "").toLowerCase().includes(search.toLowerCase()) || (r.items || []).some(i => (i.material_name || "").toLowerCase().includes(search.toLowerCase()));
    return matchStatus && matchCliente && matchObra && matchPresupuesto && matchUrgency && matchSearch;
  });
  // BLOQUE 50 PARTE B — pendientes de aprobación/modificar siempre arriba, lo
  // aprobado/rechazado abajo; dentro de cada grupo, el más nuevo primero.
  const filtered = sortPendingFirst(filteredUnsorted, r => ["borrador", "pendiente", "modificar"].includes(r.status));

  const statusCounts = requests.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const hasFilters = filterCliente || filterObra || filterPresupuesto || filterUrgency || filterStatus !== "todos" || search;
  const clearFilters = () => { setFilterStatus("todos"); setFilterCliente(""); setFilterObra(""); setFilterPresupuesto(""); setFilterUrgency(""); setSearch(""); };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              placeholder="Buscar pedidos..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] w-48"
              data-testid="pedidos-search"
            />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white" data-testid="pedidos-filter">
            <option value="todos">{`Todos (${requests.length})`}</option>
            <option value="borrador">{`Borradores (${statusCounts.borrador || 0})`}</option>
            <option value="pendiente">{`Pendientes (${statusCounts.pendiente || 0})`}</option>
            <option value="modificar">{`A Modificar (${statusCounts.modificar || 0})`}</option>
            <option value="aprobada">{`Aprobados (${statusCounts.aprobada || 0})`}</option>
            <option value="rechazada">{`Rechazados (${statusCounts.rechazada || 0})`}</option>
          </select>
          <FiltroClienteObraPresupuesto
            listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
            filterCliente={filterCliente} setFilterCliente={setFilterCliente}
            filterObra={filterObra} setFilterObra={setFilterObra}
            filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
            testIdPrefix="pedidos"
          />
          <select value={filterUrgency} onChange={e => setFilterUrgency(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white" data-testid="pedidos-urgency-filter">
            <option value="">Todas las urgencias</option>
            <option value="critica">Crítico</option>
            <option value="alta">Alta</option>
            <option value="normal">Normal</option>
          </select>
          {hasFilters && (
            <button onClick={clearFilters}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros">
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
        {canCreate && (
          <button onClick={() => { setRespaldoLocal(leerRespaldoLocal(user?._id)); setShowNew(true); }}
            className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-xl text-xs font-semibold transition-all shadow-sm hover:shadow flex-shrink-0"
            data-testid="new-pedido-btn">
            <Plus size={14} /> Nuevo Pedido
          </button>
        )}
      </div>

      {/* Pipeline visual miniatura */}
      <div className="bg-gradient-to-r from-[#1E3A5F]/5 to-[#2E6DA4]/5 rounded-xl border border-[#2E6DA4]/10 p-4">
        <p className="text-xs font-semibold text-[#2E6DA4] mb-3">Flujo del Pedido de Materiales</p>
        <div className="flex items-center gap-1 overflow-x-auto">
          {FLOW_STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-shrink-0">
              <div className="flex flex-col items-center min-w-[52px]">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: s.color }}>
                  {i + 1}
                </div>
                <span className="text-[8px] text-center mt-1 text-gray-500 leading-tight max-w-[50px]">{s.short}</span>
              </div>
              {i < FLOW_STEPS.length - 1 && <div className="h-px w-4 bg-gray-300 mb-3 flex-shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* Lista de pedidos */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-28" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
          <ClipboardList size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No hay pedidos de materiales</p>
          <p className="text-xs text-gray-400 mt-1">Cree un nuevo pedido para iniciar el proceso de compras</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div key={r._id} className="bg-white rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all" data-testid="pedido-card">
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-gray-900 text-sm font-mono">{r.number}</p>
                      <StatusBadge status={r.status} />
                      {r.cubierto_por_stock && (
                        <span className="text-[10px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-bold border border-teal-200 flex items-center gap-1" data-testid={`stock-interno-badge-${r._id}`}>
                          <Boxes size={10} /> Stock interno
                        </span>
                      )}
                      {r.cubierto_por_stock && (
                        <button
                          type="button"
                          onClick={() => handleGenerateRSI(r._id)}
                          disabled={generatingRSI === r._id}
                          className="flex items-center gap-1 text-[10px] bg-amber-100 hover:bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-bold border border-amber-200 disabled:opacity-60 transition-colors"
                          data-testid={`generate-rsi-pm-btn-${r._id}`}
                          title="Generar/reimprimir la solicitud de retiro de stock interno (aval)"
                        >
                          {generatingRSI === r._id ? <Loader2 size={10} className="animate-spin" /> : <FileText size={10} />}
                          Solicitud de retiro
                        </button>
                      )}
                      {r.urgency === "alta" && <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold border border-orange-200">ALTA</span>}
                      {r.urgency === "critica" && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold border border-red-200">CRÍTICO</span>}
                    </div>
                    {r.obra_rapida && (
                      <button
                        type="button"
                        onClick={() => canLink && setVincularTarget(r)}
                        disabled={!canLink}
                        className={`mt-2 inline-flex items-center gap-2 bg-sky-100 text-sky-800 border border-sky-300 rounded-full pl-3 pr-2 py-1 text-[11px] font-bold shadow-sm ${canLink ? "hover:bg-sky-200 cursor-pointer" : "cursor-default"} transition-colors`}
                        title={canLink ? "Vincular a un presupuesto aprobado" : "Pedido de obra rápida sin vincular"}
                        data-testid={`obra-rapida-badge-${r._id}`}>
                        <Cloud size={13} className="text-sky-600" />
                        <span>Pedido para obra rápida / A vincular con presupuesto</span>
                        {canLink && (
                          <span className="ml-0.5 bg-sky-600 text-white rounded-full w-5 h-5 flex items-center justify-center" data-testid={`vincular-icon-${r._id}`}>
                            <Link2 size={12} />
                          </span>
                        )}
                      </button>
                    )}
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-xs text-gray-600 font-medium">{r.project_name}</span>
                      {r.asistencia_number && <span className="text-[10px] font-mono font-bold bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 rounded-full" title="Orden de trabajo de asistencia" data-testid={`pm-ot-badge-${r._id}`}>OT {r.asistencia_number}</span>}
                      <span className="text-xs text-gray-400">{r.items?.length || 0} items</span>
                      {r.target_date && <span className="text-xs text-gray-400 flex items-center gap-1"><Calendar size={10} />{formatDate(r.target_date)}</span>}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">Solicitado por {r.requested_by_name} · {formatDate(r.created_at)}</p>
                    {r.modification_comment && r.status === "modificar" && (
                      <div className="mt-2 bg-orange-50 rounded-lg px-3 py-2 border border-orange-100">
                        <p className="text-xs text-orange-700 font-medium">Comentario: {r.modification_comment}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Fix S (2026-09-01): un borrador se reabre y completa ("Editar"),
                        o se confirma directamente si ya está completo. */}
                    {/* Fix DB (2026-09-16): un pedido devuelto para modificar tenía solo
                        el ojito. Ahora lleva los mismos dos botones que un borrador:
                        corregirlo y reenviarlo a aprobación. */}
                    {(r.status === "borrador" || r.status === "modificar") && canCreate && (
                      <>
                        <button onClick={() => openBorradorForEdit(r)} title={r.status === "modificar" ? "Corregir el pedido" : "Editar borrador"}
                          className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors"
                          data-testid={`editar-borrador-btn-${r._id}`}>
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => handleConfirmBorrador(r)} disabled={confirmingBorrador === r._id}
                          title={r.status === "modificar" ? "Reenviar para aprobación" : "Confirmar pedido"}
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors disabled:opacity-60"
                          data-testid={`confirmar-borrador-btn-${r._id}`}>
                          {confirmingBorrador === r._id ? <Loader2 size={15} className="animate-spin" /> : <CheckCheck size={15} />}
                        </button>
                      </>
                    )}
                    <button onClick={() => setViewRequest(r)} title="Ver pedido formal"
                      className="p-1.5 rounded-lg hover:bg-blue-50 text-[#2E6DA4] transition-colors"
                      data-testid="ver-pedido-btn">
                      <Eye size={15} />
                    </button>
                    <button onClick={() => descargarPedidoPDF(r)} title="Descargar PDF"
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                      data-testid="pdf-pedido-btn">
                      <Download size={15} />
                    </button>
                    <button onClick={() => setExpanded(expanded === r._id ? null : r._id)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                      {expanded === r._id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {canDelete && (
                      <button onClick={() => setDeleteTarget(r)} title="Eliminar pedido"
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                        data-testid={`delete-pedido-btn-${r._id}`}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Status Timeline */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <StatusTimeline currentStatus={r.status} />
                </div>
              </div>

              {/* Expanded items */}
              {expanded === r._id && r.items?.length > 0 && (
                <div className="border-t border-gray-100 px-4 pb-4">
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-400 uppercase">
                          <th className="text-left py-1 pr-3 w-6">#</th>
                          <th className="text-left py-1 pr-3">Descripción</th>
                          <th className="text-left py-1 pr-3">UM</th>
                          <th className="text-left py-1 pr-3">Cant.</th>
                          <th className="text-left py-1 pr-3">Estado</th>
                          <th className="text-center py-1 pr-3 text-lime-700">Retirado</th>
                          <th className="text-center py-1 pr-3 text-emerald-700">Entregado</th>
                          <th className="text-left py-1 pr-3">Sector</th>
                          <th className="text-left py-1 pr-3 w-10">COD</th>
                          <th className="text-left py-1 pr-3">Frente</th>
                          <th className="text-left py-1">Marca</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {r.items.map((item, i) => {
                          const fr = splitFrenteCod(item.front || r.material_type || "");
                          return (
                            <tr key={i} className="text-gray-700">
                              <td className="py-1.5 pr-3 font-bold text-[#2E6DA4]">{i + 1}</td>
                              <td className="py-1.5 pr-3 font-medium">{item.material_name}</td>
                              <td className="py-1.5 pr-3 text-gray-500">{item.unit}</td>
                              <td className="py-1.5 pr-3 font-semibold">{item.quantity}</td>
                              <SeguimientoCeldas f={seguimiento[r._id]?.items?.[i]} cant={item.quantity} cargando={!seguimiento[r._id]} />
                              <td className="py-1.5 pr-3 text-gray-500">{item.sector || "—"}</td>
                              <td className="py-1.5 pr-3 font-mono text-[#2E6DA4] font-bold">{fr.cod || "—"}</td>
                              <td className="py-1.5 pr-3 text-gray-500">{fr.label || "—"}</td>
                              <td className="py-1.5 text-gray-500 italic">{item.marca_recomendada || "—"}</td>
                            </tr>
                          );
                        })}
                        {/* Fix HY: lo que está en las OC del pedido con otro nombre o se agregó en el análisis */}
                        {(seguimiento[r._id]?.extras || []).length > 0 && (
                          <tr data-testid={`pm-seg-extras-${r._id}`}>
                            <td colSpan={11} className="pt-3 pb-1 text-[10px] font-semibold text-gray-400 uppercase">
                              En las órdenes de compra de este pedido con otro nombre o agregados en el análisis
                            </td>
                          </tr>
                        )}
                        {(seguimiento[r._id]?.extras || []).map((x, k) => (
                          <tr key={`x${k}`} className="text-gray-700" data-testid="pm-seg-extra">
                            <td className="py-1.5 pr-3 text-gray-300">+</td>
                            <td className="py-1.5 pr-3 font-medium">{x.item_name}</td>
                            <td className="py-1.5 pr-3 text-gray-500">{x.unit}</td>
                            <td className="py-1.5 pr-3 font-semibold">{segCant(x.quantity)}</td>
                            <SeguimientoCeldas f={x} cant={x.quantity} />
                            <td colSpan={4} className="py-1.5 text-gray-300">{(x.ocs || []).join(", ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {r.notes && <p className="text-xs text-gray-500 mt-2 italic">{r.notes}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal VER Pedido */}
      <PedidoDetailModal open={!!viewRequest} onClose={() => setViewRequest(null)} request={viewRequest} />

      {/* Modal Nuevo Pedido / Completar Borrador */}
      {/* Fix DB (2026-09-16): el mismo formulario, con el cartel que corresponde. */}
      <Modal open={showNew} onClose={closeNewModal}
        title={!editingBorradorId ? "Nuevo Pedido de Materiales"
          : editingStatus === "modificar" ? "Corregir Pedido Devuelto" : "Completar Borrador de Pedido"}
        extraWide fullWidth>
        <form onSubmit={handleSave} className="space-y-5">
          {/* Fix AD (2026-09-03): quedó un pedido a medio cargar en este navegador
              (se cerró la pestaña o se recargó la página). Se ofrece recuperarlo;
              nunca se aplica solo. */}
          {!editingBorradorId && respaldoLocal && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pm-respaldo-banner">
              <div className="flex items-start gap-2.5 flex-1">
                <FileText size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Tenés un pedido sin terminar del {formatDateTime(respaldoLocal.at)}, con{" "}
                  {(respaldoLocal.form.items || []).filter(i => (i.material_name || "").trim()).length} material(es) cargado(s).
                  Se guardó en este dispositivo cuando la ventana se cerró sin guardar.
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button type="button" onClick={() => { setForm(respaldoLocal.form); setRespaldoLocal(null); toast.success("Pedido recuperado"); }}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold"
                  data-testid="pm-respaldo-recuperar">Recuperar</button>
                <button type="button" onClick={() => { setRespaldoLocal(null); borrarRespaldoLocal(); }}
                  className="border border-amber-300 text-amber-700 hover:bg-amber-100 rounded-lg px-3 py-1.5 text-xs font-semibold"
                  data-testid="pm-respaldo-descartar">Descartar</button>
              </div>
            </div>
          )}
          {/* Datos del encabezado */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha Objetivo</label>
              <input type="date" value={form.target_date} onChange={e => setForm({ ...form, target_date: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="pedido-date-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nivel de Urgencia</label>
              <select value={form.urgency} onChange={e => setForm({ ...form, urgency: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white">
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="critica">Crítico</option>
              </select>
            </div>
            {/* Fix O (2026-08-31): punto 14 de la carta — "al cargar el pedido se
                registra el tiempo estimado de uso del material, y cumplido ese plazo
                el sistema avisa a logística para retirar lo que haya sobrado". Todo
                el circuito estaba escrito en el backend (dias_uso_estimado, la
                fecha límite al entregar y la alerta) pero no había dónde cargar el
                dato, así que la cadena entera quedaba en nulo. Es opcional. */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Días de uso <span className="text-gray-400 font-normal">(opc.)</span></label>
              <input type="number" min="1" value={form.dias_uso_estimado} onChange={e => setForm({ ...form, dias_uso_estimado: e.target.value })}
                placeholder="Ej: 30"
                title="Cuántos días se va a usar el material en obra. Cumplido el plazo, logística recibe el aviso para retirar el sobrante."
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="pedido-dias-uso-input" />
            </div>
          </div>

          {/* BLOQUE 16 — Checkbox obra rápida (excepción a la validación de presupuesto) */}
          <label className="flex items-start gap-2.5 bg-sky-50 border border-sky-200 rounded-xl p-3 cursor-pointer" data-testid="obra-rapida-checkbox-label">
            <input
              type="checkbox"
              checked={form.obra_rapida}
              onChange={e => setForm({ ...form, obra_rapida: e.target.checked, ...(e.target.checked ? { lista_cliente_id: "", lista_obra_id: "", presupuesto_id: "", presupuesto_codigo: "", pto_objetivo: "", items: form.items.map(i => i.categoria === "listado" ? { ...i, categoria: "menores", item_uid: null } : i) } : { obra_rapida_nombre: "" }) })}
              className="mt-0.5 w-4 h-4 accent-sky-600"
              data-testid="obra-rapida-checkbox"
            />
            <span className="flex items-center gap-1.5 text-xs font-semibold text-sky-800">
              <Cloud size={14} className="text-sky-600" />
              Pedido para obra rápida / a vincular posteriormente
            </span>
          </label>

          {form.obra_rapida ? (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 space-y-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Nombre de la obra <span className="text-red-600">*</span></label>
              <input
                type="text"
                value={form.obra_rapida_nombre}
                onChange={e => setForm({ ...form, obra_rapida_nombre: e.target.value })}
                placeholder="Ej: Reforma local Sergio Doria"
                className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 ${!form.obra_rapida_nombre.trim() ? "border-red-300" : "border-gray-300"}`}
                data-testid="obra-rapida-nombre-input"
              />
              <p className="text-[10px] text-sky-700">Este pedido avanzará el flujo completo sin presupuesto. Podrás vincularlo retroactivamente cuando exista el presupuesto aprobado.</p>
            </div>
          ) : (
          /* Fix G (2026-08-30): la vinculación al presupuesto pasa a ser opcional.
             Obligatorio: cliente y obra. */
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
              <span>Vinculación con Presupuesto</span>
            </p>
            {/* Fix Z (2026-09-02): imputar el pedido a una orden de trabajo de asistencia
                (Ingeniería → Asistencias). Con OT, la obra es opcional. */}
            {(otAbiertas.length > 0 || form.asistencia_id) && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Orden de trabajo (asistencia) <span className="text-gray-400 font-normal">(opcional)</span></label>
                {/* Fix DY (2026-09-23): desplegable con buscador, mismo valor. */}
                <SelectBuscable
                  value={form.asistencia_id}
                  onChange={v => { const ot = otAbiertas.find(o => o.id === v); setForm({ ...form, asistencia_id: v, asistencia_number: ot?.number || "" }); }}
                  vacio="— Sin orden de trabajo —"
                  etiquetaActual={form.asistencia_number || "OT"}
                  options={otAbiertas.map(o => ({ value: o.id, label: `${o.number} · ${o.descripcion}${o.obra ? ` · ${o.obra}` : ""}` }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
                  testId="pedido-asistencia-select" />
                {form.asistencia_id && <p className="text-[10px] text-gray-500 mt-1">Pedido imputado a la OT {form.asistencia_number}: la obra es opcional y los materiales van a la solicitud de presupuesto al cerrar la asistencia.</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Cliente (Catálogo)</label>
                <SelectBuscable
                  value={form.lista_cliente_id}
                  onChange={v => setForm({ ...form, lista_cliente_id: v, lista_obra_id: "" })}
                  vacio="— Sin vincular —"
                  options={listasClientes.map(c => ({ value: c._id, label: `${c.codigo} · ${c.cliente}` }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
                  testId="pedido-lista-cliente-select" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Obra (Catálogo)</label>
                <SelectBuscable
                  value={form.lista_obra_id}
                  onChange={v => {
                    // Fix S (2026-09-01): cambiar la obra desvincula el presupuesto en
                    // silencio (comportamiento preexistente) — con presupuesto ya
                    // elegido, las filas "listado" quedaban degradadas sin aviso. Se
                    // agrega el aviso; el reset en sí no se toca (pre-existente).
                    if (form.presupuesto_id) toast.error("Cambiaste la obra: se desvinculó el presupuesto y las filas del listado quedan sin imputar.");
                    setForm({ ...form, lista_obra_id: v, presupuesto_id: "", presupuesto_codigo: "" });
                  }}
                  vacio="— Sin vincular —"
                  options={obrasFiltradas.map(o => ({ value: o._id, label: `${o.codigo} · ${o.obra}` }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
                  testId="pedido-lista-obra-select" />
                {presupuestoEsVarios && !form.lista_obra_id && (
                  <p className="text-[10px] text-gray-500 mt-1" data-testid="pedido-obra-opcional-varios">Presupuesto de tipo Varios: la obra es opcional.</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Presupuesto Objetivo <span className="text-gray-400 font-normal">(opcional)</span></label>
              <SelectBuscable
                value={form.presupuesto_id}
                onChange={v => {
                  const sel = presupuestosFiltrados.find(b => (b._id || b.id) === v);
                  // Fix S (2026-09-01): la promoción a "listado" en filas vacías se
                  // movió al efecto que trae items-para-pedido (más abajo), porque
                  // recién ahí se sabe si el presupuesto tiene líneas imputables.
                  // Acá solo se limpia el item_uid de filas "listado" (apuntaban al
                  // presupuesto anterior).
                  setForm({ ...form, presupuesto_id: v, presupuesto_codigo: sel?.number || "", pto_objetivo: sel?.number || "",
                    items: form.items.map(i => i.categoria === "listado" ? { ...i, item_uid: null, item_uids: null } : i) });
                }}
                vacio={loadingBudgets ? "Cargando..." : "— Sin presupuesto vinculado —"}
                etiquetaActual={form.presupuesto_codigo}
                options={presupuestosFiltrados.map(b => ({ value: b._id || b.id, label: `${b.number} · ${b.nombre || b.obra}` }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white"
                testId="pedido-presupuesto-select"
                disabled={loadingBudgets} />
              {/* Fix G (2026-08-30): ya no bloquea. Se avisa que sin presupuesto
                  no se puede imputar contra el listado ni medir consumo, pero el
                  pedido se puede crear igual. */}
              {selectedObra && presupuestosFiltrados.length === 0 && (
                <p className="text-[10px] text-amber-600 mt-1" data-testid="pedido-sin-aprobados-warning">
                  La obra "{selectedObra.obra}" todavía no tiene presupuestos aprobados. Podés crear el pedido igual, con categoría Material menor, Imprevisto o Equipamiento.
                </p>
              )}
              {!form.presupuesto_id && (
                <p className="text-[10px] text-gray-500 mt-1">Sin presupuesto vinculado no se puede imputar contra el listado de materiales ni medir el consumo.</p>
              )}
              {/* CEN 2.0 FASE 3 PARTE A — marcas cotizadas de este presupuesto, solo lectura */}
              {form.presupuesto_id && (budgetPedidoData.marcas_cotizadas || budgetPedidoData.marcas_cotizadas_split) && (
                <div className="mt-2 bg-white border border-blue-200 rounded-lg p-2.5" data-testid="pedido-marcas-cotizadas-box">
                  <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wide mb-1">Marcas Cotizadas (referencia)</p>
                  {budgetPedidoData.marcas_cotizadas_split
                    ? <div className="grid grid-cols-2 gap-1 text-[10px] text-gray-600">
                        {Object.entries(budgetPedidoData.marcas_cotizadas_split).filter(([, v]) => (v || "").trim()).map(([k, v]) => (
                          <span key={k}><strong className="capitalize">{k}:</strong> {v}</span>
                        ))}
                      </div>
                    : <p className="text-[10px] text-gray-600">{budgetPedidoData.marcas_cotizadas}</p>}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Tabla de ítems */}
          <div>
            {/* Fix BS (2026-09-09): "Esas dos filas deberian quedar fijas. La de
                arriba falta fijar, donde esta agregar item" (Edson, 09/09). En
                pantalla grande se pega debajo de la barra de título del modal
                (66 px) con altura fija de 40 px, que es el desfasaje que después
                usa el encabezado de columnas. En pantalla angosta queda como
                estaba: ahí la grilla tiene su propio contenedor desplazable. */}
            <div className="flex items-center justify-between mb-2 lg:mb-0 lg:h-10 lg:sticky lg:top-[66px] lg:z-30 lg:bg-white lg:border-b lg:border-gray-100" data-testid="pm-acciones-fijas">
              <label className="text-xs font-semibold text-gray-700">Materiales Solicitados *
                {/* Fix AS (2026-09-05): con presupuesto vinculado, un material cargado
                    como menor/imprevisto/equipamiento/herramienta no entra en el
                    consumo de la obra. No bloquea el pedido: avisa. */}
                {form.presupuesto_id && form.items.some(i => i.categoria !== "listado" && (i.material_name || "").trim()) && (
                  <span className="ml-2 text-[10px] font-semibold text-amber-700" data-testid="pm-aviso-sin-imputar">
                    · {form.items.filter(i => i.categoria !== "listado" && (i.material_name || "").trim()).length} sin imputar al presupuesto
                  </span>
                )}
              </label>
              <div className="flex items-center gap-3">
                {/* Fix G (2026-08-30): "con el pto vinculado se debe poder desplegar
                    todos los ítems del pto y marcar el correspondiente al cual imputar". */}
                {form.presupuesto_id && (budgetPedidoData.items || []).length > 0 && (
                  <button type="button" onClick={openItemPicker} className="flex items-center gap-1 text-xs text-[#2E6DA4] hover:text-[#245A8A] font-semibold" data-testid="ver-items-presupuesto-btn">
                    <List size={12} /> Ver ítems del presupuesto ({(budgetPedidoData.items || []).length})
                  </button>
                )}
                <button type="button" onClick={() => setShowImport(true)} className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 font-semibold" data-testid="import-items-btn">
                  <FileUp size={12} /> Importar desde Excel/PDF
                </button>
                <button type="button" onClick={addItem} className="flex items-center gap-1 text-xs text-[#2E6DA4] hover:text-[#245A8A] font-semibold" data-testid="agregar-item-btn">
                  <Plus size={12} /> Agregar ítem
                </button>
              </div>
            </div>
            {/* Fix AY (2026-09-06): "solamente esa fila debe quedar fijada cuando
                se desplaza para abajo (…) todo eso fijo puede desplazarse y quedar
                oculto para no ocupar demasiado espacio" (Edson, 06/09).
                El Fix S le había dado a la grilla su propio scroll con altura
                acotada, y por eso el bloque de arriba (fecha objetivo, urgencia,
                vinculación) no se iba nunca: había dos zonas desplazables.
                En pantalla grande se saca ese contenedor y el único scroll pasa a
                ser el del modal, con el encabezado fijo debajo de su barra de
                título. En pantalla angosta se conserva el desplazamiento
                horizontal, que es lo que evita que se corten columnas en celular. */}
            <div className="overflow-x-auto lg:overflow-visible relative">
            <div className="space-y-2 min-w-[1180px]">
              {/* Fix S (2026-09-01): encabezado FIJO. El cliente: "que el encabezado
                  quede fijo porque cuando la lista es larga hay que subir todo para
                  agregar más filas". Mismo criterio que el fix I en el detalle del
                  presupuesto. */}
              {/* Fix AY (2026-09-06): 66 px es lo que mide la barra de título del
                  modal (`py-4` + la fila de 34 px + el borde), que también es
                  sticky. Sin ese desfasaje el encabezado quedaría tapado. */}
              {/* Fix BS (2026-09-09): 106 = 66 de la barra de título del modal + 40
                  de la barra de acciones, que ahora también es sticky. El `top-0`
                  de pantalla angosta no cambia: ahí la barra de acciones no se
                  fija y el encabezado sigue pegado al borde de su contenedor. */}
              <div className="grid gap-1 text-[10px] font-semibold text-gray-500 uppercase px-2 sticky top-0 lg:top-[106px] z-20 bg-white py-2 border-b border-gray-200 shadow-sm" data-testid="pm-grid-header" style={{ gridTemplateColumns: "24px 108px minmax(0,3fr) 56px 64px minmax(0,2fr) 88px 96px 116px minmax(0,1.2fr) minmax(0,1.5fr) 22px" }}>
                <span>#</span>
                <span>Categ.</span>
                <span>Descripción</span>
                <span>UM</span>
                <span>Cant.</span>
                <span>Ítem del Pto</span>
                {/* Fix A (2026-08-27): se explicita que son opcionales. Ninguno
                    de los dos se valida ni en el frontend ni en el backend; el
                    cliente creía que Nivel era obligatorio y frenaba los pedidos.
                    Fix G (2026-08-30): Tarea y Observación, también opcionales. */}
                <span>Sector <span className="text-gray-400 font-normal">(opc.)</span></span>
                {/* Fix DG (2026-09-16): el nivel ya no se carga, se hereda del ítem. */}
                <span>Nivel <span className="text-gray-400 font-normal">(del ítem)</span></span>
                <span>Tarea <span className="text-gray-400 font-normal">(opc.)</span></span>
                <span>Marca <span className="text-gray-400 font-normal">(opc.)</span></span>
                <span>Observación <span className="text-gray-400 font-normal">(opc.)</span></span>
                <span></span>
              </div>
              <DndContext sensors={pmSensors} collisionDetection={closestCenter} onDragEnd={handleDragEndPM}>
              <SortableContext items={pmSortIds} strategy={verticalListSortingStrategy}>
              {form.items.map((item, i) => (
                <PMItemRow
                  key={item.__uid || i}
                  dragId={pmRowId(item, i)}
                  hayCopia={itemsCopiados ? itemsCopiados.length : 0}
                  copiarItems={copiarItems}
                  pegarItems={pegarItems}
                  tareaCopiada={tareaCopiada}
                  copiarTarea={copiarTarea}
                  pegarTarea={pegarTarea}
                  item={item}
                  idx={i}
                  canRemove={form.items.length > 1}
                  suggestions={sugerenciasConStock}
                  stockMap={stockInfo.map}
                  listadoSuggestions={listadoSuggestions}
                  itemsDelPto={budgetPedidoData.items || []}
                  nivelOptions={budgetPedidoData.niveles || []}
                  nivelMap={nivelMap}
                  pesosItem={item.material_name ? (pesosPorMaterialRef.current[item.material_name] || null) : null}
                  tareaOptions={tareaOptions}
                  hasPresupuesto={!!form.presupuesto_id}
                  consumoRow={item.item_uid ? consumoMap[item.item_uid] : null}
                  consumoMatRow={item.material_name ? consumoMatMap[item.material_name] : null}
                  updateItem={updateItem}
                  updateItemCategoria={updateItemCategoria}
                  updateItemMaterial={updateItemMaterial}
                  removeItem={removeItem}
                  setItemsMultiples={setItemsMultiples}
                />
              ))}
              </SortableContext>
              </DndContext>
            </div>
            </div>
            {/* Fix BD (2026-09-07): se explica la categoría nueva y cuándo usar cada una. */}
            <p className="text-[10px] text-gray-400 mt-1.5">Cada ítem debe tener una <strong>categoría</strong>. "Del Listado de Mat. del Pto" referencia un ítem exacto del presupuesto en la columna "Ítem del Pto" (obligatorio para medir el consumo). <strong>"Materiales de Obra"</strong> es para las obras que todavía no tienen presupuesto vinculado: se carga ahora y se imputa después, desde "Imputar compras anteriores" de la obra.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              placeholder="Detalles adicionales del pedido..."
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" />
          </div>

          {/* Fix G (2026-08-30): listado completo de ítems del presupuesto con
              marcado múltiple. Solo lectura de descripción, unidad y cantidad:
              no muestra ningún precio, igual que el endpoint que lo alimenta. */}
          {showItemPicker && (
            <Modal open={showItemPicker} onClose={() => setShowItemPicker(false)} title="Ítems del presupuesto" wide>
              <div className="space-y-3">
                <input
                  value={pickerSearch}
                  onChange={e => setPickerSearch(e.target.value)}
                  placeholder="Filtrar por número o descripción..."
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                  data-testid="item-picker-search"
                />
                <div className="border border-gray-200 rounded-xl max-h-[55vh] overflow-y-auto divide-y divide-gray-100" data-testid="item-picker-list">
                  {pickerItems.length === 0 && (
                    <p className="text-xs text-gray-400 p-4 text-center">No hay ítems que coincidan.</p>
                  )}
                  {pickerItems.map(bi => (
                    <label key={bi.item_uid} className="flex items-start gap-2.5 p-2.5 hover:bg-blue-50/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!pickerMarked[bi.item_uid]}
                        onChange={e => setPickerMarked(m => ({ ...m, [bi.item_uid]: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 accent-[#2E6DA4]"
                        data-testid={`item-picker-check-${bi.item_uid}`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs text-gray-800">
                          {bi.numero ? <strong className="text-[#2E6DA4] mr-1">{bi.numero}</strong> : null}
                          {bi.descripcion}
                        </span>
                        <span className="block text-[10px] text-gray-400 mt-0.5">
                          {bi.cantidad} {bi.unidad}
                          {consumoMap[bi.item_uid] ? ` · comprado ${consumoMap[bi.item_uid].pct_comprado}%` : ""}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowItemPicker(false)}
                    className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
                  <button type="button" onClick={confirmItemPicker} disabled={pickerCount === 0}
                    className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50"
                    data-testid="item-picker-confirm">
                    Agregar {pickerCount > 0 ? pickerCount : ""} al pedido
                  </button>
                </div>
              </div>
            </Modal>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={closeNewModal} data-testid="pm-form-cancelar-btn"
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            {/* Fix S (2026-09-01): "¿Se puede quedar en borrador una lista que
                iniciás y por a o b motivo no pudiste terminar?" — Edson, 01/09.
                Sin ninguna validación: guarda tal como está. */}
            <button type="button" onClick={handleSaveBorrador} disabled={saving}
              className="flex-1 border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
              data-testid="save-borrador-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
              {editingBorradorId ? "Guardar cambios" : "Guardar borrador"}
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
              data-testid="save-pedido-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <ClipboardList size={15} />}
              {saving ? "Enviando..." : (!editingBorradorId ? "Solicitar"
                : editingStatus === "modificar" ? "Reenviar para aprobación" : "Confirmar pedido")}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Importar desde Excel/PDF (BLOQUE 1 PARTE A) */}
      <ImportPMItemsModal open={showImport} onClose={() => setShowImport(false)} onConfirm={handleImportedItems} />

      {/* BLOQUE 63 — aviso de posible texto invertido al guardar. Nunca corrige
          solo: cada sugerencia se acepta o se descarta a mano, ítem por ítem. */}
      <Modal open={!!reversalSuspects} onClose={() => setReversalSuspects(null)} title="Revisá estas descripciones antes de guardar" data-testid="reversal-warning-modal">
        {reversalSuspects && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                Estas descripciones parecen estar escritas al revés (letra por letra) — es posible que el
                teclado del celular haya invertido el texto mientras escribías. Elegí "Usar sugerencia" solo
                si la corrección es correcta; si no, dejalo como está.
              </p>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {reversalSuspects.map((s, k) => (
                <div key={k} className="border border-gray-200 rounded-xl p-3" data-testid={`reversal-suspect-${k}`}>
                  <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1">Ítem {s.idx + 1}</p>
                  <p className="text-xs text-gray-500">Escrito: <span className="font-mono text-red-600">{s.current}</span></p>
                  <p className="text-xs text-gray-500 mb-2">¿Quisiste escribir: <span className="font-mono text-emerald-700">{s.proposed}</span>?</p>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!s.accept}
                      onChange={e => setReversalSuspects(prev => prev.map((p, i) => i === k ? { ...p, accept: e.target.checked } : p))}
                      data-testid={`reversal-accept-${k}`}
                    />
                    Usar la sugerencia para este ítem
                  </label>
                </div>
              ))}
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setReversalSuspects(null)}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium"
                data-testid="reversal-cancel-btn">Volver a editar</button>
              <button type="button" onClick={confirmReversalAndSave} disabled={saving}
                className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
                data-testid="reversal-continue-btn">
                {saving ? "Guardando..." : "Continuar y guardar"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Fix AD (2026-09-03): aviso al cerrar la ventana del pedido con algo
          cargado. Pedido textual de Edson (03/09): "para que no se llene de
          borradores que salga una ventana, desea guardar en borrador o salir sin
          guardar". El borrador se crea solo si él lo elige. */}
      <Modal open={confirmCerrar} onClose={() => setConfirmCerrar(false)} title="El pedido no está guardado" wide data-testid="pm-confirm-cerrar-modal">
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              Tenés {form.items.filter(i => (i.material_name || "").trim()).length} material(es) cargado(s) sin guardar.
              {editingBorradorId
                ? " Este borrador ya existe en el sistema: guardá los cambios para no perderlos, o salí sin guardar y quedan sin aplicar."
                : " Guardalo como borrador para seguir después desde \"Editar borrador\", o salí sin guardar y se descarta."}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button type="button" onClick={() => setConfirmCerrar(false)}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium"
              data-testid="pm-confirm-seguir">Seguir editando</button>
            <button type="button" onClick={descartarYCerrar}
              className="flex-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-xl py-2.5 text-sm font-semibold"
              data-testid="pm-confirm-salir">Salir sin guardar</button>
            <button type="button" onClick={handleSaveBorrador} disabled={saving}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="pm-confirm-guardar-borrador">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
              {saving ? "Guardando..." : "Guardar borrador"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal confirmar eliminación (BLOQUE 1 PARTE E) */}
      <Modal open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} title="Eliminar Pedido de Materiales">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                ¿Confirmás eliminar el pedido <span className="font-mono font-bold">{deleteTarget.number}</span>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button type="button" onClick={handleDeleteRequest} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-delete-pedido-btn">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Vinculación Retroactiva (BLOQUE 16) */}
      <VincularRetroactivoModal
        target={vincularTarget}
        onClose={() => setVincularTarget(null)}
        listasClientes={listasClientes}
        listasObras={listasObras}
        allBudgets={allBudgets}
        onLinked={() => { setVincularTarget(null); onRefresh(); }}
      />
    </div>
  );
}

export function VincularRetroactivoModal({ target, onClose, listasClientes, listasObras, allBudgets, onLinked }) {
  const [clienteId, setClienteId] = useState("");
  const [obraId, setObraId] = useState("");
  const [presupuestoId, setPresupuestoId] = useState("");
  const [saving, setSaving] = useState(false);
  // Fix EJ (2026-09-27): "también vincule cada línea del pedido rápido a un ítem del pto
  // como en un pedido normal" (CEN). Las líneas de material eligen su ítem del presupuesto.
  const [ptoData, setPtoData] = useState({ items: [], materiales: [], niveles: [] });
  const [lineaItem, setLineaItem] = useState({});

  useEffect(() => {
    setClienteId(""); setObraId(""); setPresupuestoId("");
  }, [target]);

  useEffect(() => {
    setLineaItem({});
    if (!presupuestoId) { setPtoData({ items: [], materiales: [], niveles: [] }); return; }
    axios.get(`${API}/budgets/${presupuestoId}/items-para-pedido`, { withCredentials: true })
      .then(({ data }) => setPtoData({ items: data.items || [], materiales: data.materiales || [], niveles: data.niveles || [] }))
      .catch(() => toast.error("No se pudieron cargar los ítems del presupuesto"));
  }, [presupuestoId]);

  const lineas = (Array.isArray(target?.lineas) ? target.lineas
    : (Array.isArray(target?.items) ? target.items.map((it, idx) => ({
        idx, nombre: it.material_name || "", unidad: it.unit || "", cantidad: it.quantity, categoria: it.categoria || "", item_uid: it.item_uid || "",
      })) : []))
    .filter(l => !l.item_uid && ["", "listado", "materiales"].includes(l.categoria || ""));
  const nivelMapV = {};
  ptoData.niveles.forEach(n => { nivelMapV[n.id] = n.label; });
  const normV = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
  const deEsteMaterial = (nombre) => {
    const m = ptoData.materiales.find(x => normV(x.descripcion) === normV(nombre));
    const uids = m ? Object.keys(m.por_item_uid || {}) : [];
    return ptoData.items.filter(bi => uids.includes(bi.item_uid));
  };
  const faltanLineas = lineas.filter(l => !lineaItem[l.idx]).length;

  const obrasFiltradas = clienteId ? listasObras.filter(o => o.cliente_id === clienteId) : listasObras;
  const selectedObra = listasObras.find(o => o._id === obraId);
  const presupuestos = filtrarPresupuestos(allBudgets, { clienteId, obraSel: selectedObra, listasClientes, listasObras });   // Fix DY

  const handleLink = async () => {
    if (!presupuestoId) { toast.error("Seleccioná un presupuesto aprobado"); return; }
    if (faltanLineas) { toast.error(`Falta vincular ${faltanLineas} línea(s) a un ítem del presupuesto`); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/purchases/requests/${target._id}/vincular-retroactivo`,
        { presupuesto_id: presupuestoId, lista_cliente_id: clienteId || null, lista_obra_id: obraId || null,
          lineas: lineas.map(l => ({ idx: l.idx, item_uid: lineaItem[l.idx] })) },
        { withCredentials: true });
      const u = res.data?.updated || {};
      toast.success(`Vinculado a ${res.data?.presupuesto_codigo || "presupuesto"} · ${u.lineas || 0} línea(s) imputada(s) · ${(u.oc || 0)} OC actualizada(s)`);
      onLinked();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al vincular"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={!!target} onClose={() => !saving && onClose()} title="Vincular a Presupuesto" wide>
      {target && (
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 bg-sky-50 border border-sky-200 rounded-xl p-3">
            <Cloud size={16} className="text-sky-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-sky-800">
              Vinculás el pedido de obra rápida <span className="font-mono font-bold">{target.number}</span>
              {target.obra_rapida_nombre ? <> ("{target.obra_rapida_nombre}")</> : null} a un presupuesto aprobado.
              Se propagará a todos sus documentos derivados (PP, AP, PF, OC).
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Cliente (Catálogo)</label>
            <SelectBuscable value={clienteId} onChange={v => { setClienteId(v); setObraId(""); setPresupuestoId(""); }}
              vacio="— Seleccionar cliente —"
              options={listasClientes.map(c => ({ value: c._id, label: `${c.codigo} · ${c.cliente}` }))}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" testId="vincular-cliente-select" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Obra (Catálogo)</label>
            <SelectBuscable value={obraId} onChange={v => { setObraId(v); setPresupuestoId(""); }}
              vacio="— Seleccionar obra —"
              options={obrasFiltradas.map(o => ({ value: o._id, label: `${o.codigo} · ${o.obra}` }))}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" testId="vincular-obra-select" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Presupuesto Aprobado <span className="text-red-600">*</span></label>
            <SelectBuscable value={presupuestoId} onChange={v => setPresupuestoId(v)}
              vacio="— Seleccionar presupuesto aprobado —"
              options={presupuestos.map(b => ({ value: b._id || b.id, label: `${b.number} · ${b.nombre || b.obra}` }))}
              className={`w-full border rounded-xl px-3 py-2.5 text-sm bg-white ${!presupuestoId ? "border-red-300" : "border-gray-300"}`}
              testId="vincular-presupuesto-select" />
            {selectedObra && presupuestos.length === 0 && (
              <p className="text-[10px] text-red-600 mt-1">La obra seleccionada no tiene presupuestos aprobados.</p>
            )}
          </div>
          {presupuestoId && lineas.length > 0 && (
            <div data-testid="vincular-lineas">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Ítem del presupuesto de cada línea <span className="text-red-600">*</span></label>
              <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[40vh] overflow-y-auto">
                {lineas.map(l => {
                  const propios = deEsteMaterial(l.nombre);
                  return (
                    <div key={l.idx} className="grid grid-cols-2 gap-2 items-start px-3 py-2" data-testid={`vincular-linea-${l.idx}`}>
                      <p className="text-xs text-gray-800 font-medium">{l.nombre}<span className="block text-[10px] text-gray-400">{l.cantidad} {l.unidad}</span></p>
                      <SelectItemPto value={lineaItem[l.idx] || ""} itemsDeEsteMaterial={propios}
                        itemsRestantes={ptoData.items.filter(bi => !propios.includes(bi))} nivelMap={nivelMapV}
                        placeholder="— Imputar a —" testId={`vincular-linea-item-${l.idx}`}
                        onChange={v => setLineaItem(prev => ({ ...prev, [l.idx]: v }))} />
                    </div>
                  );
                })}
              </div>
              {faltanLineas > 0 && <p className="text-[10px] text-amber-700 mt-1">Faltan {faltanLineas} línea(s) por vincular.</p>}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={saving}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
            <button type="button" onClick={handleLink} disabled={saving || !presupuestoId || faltanLineas > 0}
              className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="confirm-vincular-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
              {saving ? "Vinculando..." : "Vincular"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
